import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { BrandLogo, EditItemSheet } from '../../components';
import { WebMobileCameraView } from './components/WebMobileCameraView';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { fpColors } from '../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  streamScan,
  streamSegmentResume,
  ScanHttpError,
} from '../../api/streamScan';
import { api } from '../../api/remembrApi';
import { captureScanLocation, type ScanLocation } from '../../utils/scanLocation';
import { apiErrorText } from '../../utils/apiErrorText';
// SaveDestinationPicker import removed — save flow always saves to
// Personal now. File kept at src/features/segment/components/
// SaveDestinationPicker.tsx in case crew routing is reintroduced.
// FE-214 — `SerialCaptureChip` removed. Backend dropped the
// `serial_number` column on BE-202; the on-scan capture flow was
// retired with it.
// loadGroups + selectGroups + selectGroupsHasLoaded imports removed
// alongside SaveDestinationPicker — Save no longer needs the user's
// group list synchronously.
import {
  mark,
  measure,
  snapshot as timingSnapshot,
  startMark,
  TimingMark,
} from '../../utils/clarityTimings';
import { clarityHelpers } from '../../utils/clarity';
import {
  confirmCompleted,
  confirmFailed,
  confirmStarted,
  enterRefine,
  exitRefine,
  itemRemoved,
  itemStreamed,
  logEvent,
  maskErrored,
  maskStreamed,
  resetSession,
  scanStarted,
  scanStatus,
  seedFromBackend,
  segmentationComplete,
  segmentationStarted,
  streamCompleted,
  streamErrored,
  summaryReceived,
  toggleSelected,
  selectConfirmedAcceptedIds,
  selectMaskErrors,
  selectMasks,
  selectPrimaryPhotos,
  selectSceneContext,
  selectScanItems,
  selectSegmentError,
  selectSegmentStatus,
  selectSelectedSet,
  selectSegment,
  type MaskRecord,
} from './segmentSlice';
// #170 — unified ShareSheet replaces SaveDestinationChip + ShareSubsetSheet.
import { ShareSheet, type SharePlan } from '../sharing/components/ShareSheet';
import { createPendingGroups } from '../sharing/commitSharePlan';
import { ImageAnnotationStage, type AnnotationItem } from './components/ImageAnnotationStage';
import { ItemRail, type ItemRailItem } from './components/ItemRail';
import { ScanningHero } from './components/ScanningHero';
import { RefineOverlay } from './components/RefineOverlay';
import { RefineToolbar, type RefineMode } from './components/RefineToolbar';
import { FloatingActionBar } from './components/FloatingActionBar';
import { DrawNewItemOverlay } from './components/DrawNewItemOverlay';
import { TapAddGhost, type TapAddRecord } from './components/TapAddGhost';
import { manufactureSquareBbox } from './manufactureSquareBbox';
import {
  NameDraftItemSheet,
  type NameDraftValues,
} from './components/NameDraftItemSheet';
import { FocusedItemCarousel } from './components/FocusedItemCarousel';
import { ImageInfoSheet } from './components/ImageInfoSheet';
import { DoneView } from './components/DoneView';
import {
  cancelPrimaryPhotoStreams,
  startPrimaryPhotoStreams,
} from './primaryPhotoService';
import { upsertInventoryItem } from '../browse/browseSlice';
import { type ZoomableStageHandle } from './components/ZoomableStage';
import { Dimensions } from 'react-native';
import type { Bbox } from './useImageTransform';
import type { InventoryItemResponse } from '../../api/types';
import type {
  AnnotationInteraction,
  AnnotationPoint,
} from '../../api/scanStreamTypes';

const POINT_CAP = 32;

/**
 * Gap #121 (2026-05-27 audit) — pretty-print a room enum slug for the
 * header chip. The BE returns labels in snake_case enum form (e.g.
 * "DINING_ROOM", "LIVING_ROOM"), which we used to render verbatim.
 * Real users don't read enum slugs. Normalise to title case + spaces:
 *   "DINING_ROOM" → "Dining room"
 *   "living_room" → "Living room"
 *   "kitchen"     → "Kitchen"
 */
function formatRoomLabel(slug: string): string {
  if (!slug) return slug;
  const cleaned = slug.replace(/_/g, ' ').toLowerCase().trim();
  if (cleaned.length === 0) return cleaned;
  return cleaned[0].toUpperCase() + cleaned.slice(1);
}

interface SelectedImage {
  uri: string;
  name: string;
  type: string;
  size?: number;
}

/**
 * Segmentation scan v2 — picker + stream consumer + review surface
 * with in-place bbox refine AND item-metadata edit via the existing
 * `EditItemSheet`.
 *
 * Behaviour (flag-gated on `EXPO_PUBLIC_SEGMENTATION_UI=1` OR
 * `localStorage.segmentation_ui=1`):
 *   1. Pick screen — Take Photo / From Library
 *   2. Scanning → Streaming → Segmenting → Complete state machine
 *   3. Bboxes + masks render live over the photo as events stream in
 *   4. Tap rail card's color tab → toggle selected
 *   5. Tap bbox's ✎ (or rail's "Refine box") → enter refine mode:
 *      RefineOverlay shows drag body + 8 resize handles with
 *      pointer events (mouse + touch). Apply → PATCH
 *      /inventory/{id}/resegment with use_box_as_mask: true; the
 *      new mask replaces the prior in the store.
 *   6. Tap rail's "Edit details" → EditItemSheet modal for
 *      name/category/value/description.
 *   7. Save CTA placeholder — real confirm-batch wires in Chunk 5.
 */

/**
 * Report a timing scorecard to Clarity. Called once per outcome
 * (e.g. on Save, on Done view mount). Each TimingMark becomes one
 * key on the metadata object; only marks that were actually
 * recorded show up. Extra context fields (action, accepted,
 * rejected) are stringified because Clarity's `trackFeature`
 * only takes string-valued metadata.
 *
 * Idempotent — clarityHelpers swallows its own errors when
 * Clarity isn't configured (e.g. local dev without DSN), so a
 * stray call from an already-reported session is harmless.
 */
function reportTimingSnapshot(
  action: 'save' | 'done' | 'error',
  extra: Record<string, number | string> = {},
): void {
  const snap = timingSnapshot();
  const metadata: Record<string, string> = { action };
  for (const [k, v] of Object.entries(snap)) metadata[k] = String(v);
  for (const [k, v] of Object.entries(extra)) metadata[k] = String(v);
  // Derived perceptual deltas the PRD names by acronym, computed
  // from the underlying marks. Easier to chart without a JOIN.
  const ttfv = measure(TimingMark.FIRST_VISUAL);
  const ttfod = measure(TimingMark.FIRST_ITEM);
  const ttff = measure(TimingMark.FIRST_MASK);
  const ttlc = measure(TimingMark.LAST_MASK);
  const e2e = measure(TimingMark.DONE);
  if (ttfv !== null) metadata.ttfv = String(ttfv);
  if (ttfod !== null) metadata.ttfod = String(ttfod);
  if (ttff !== null) metadata.ttff = String(ttff);
  if (ttlc !== null) metadata.ttlc = String(ttlc);
  if (e2e !== null) metadata.e2e = String(e2e);
  clarityHelpers.trackFeature('segment', 'timing_scorecard', metadata);
}

export const SegmentScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  // Web entry UI forks by viewport: desktop gets a drag-drop hero,
  // mobile-web gets two big OS-camera/library buttons. Native keeps
  // the legacy PickView. We tap useWindowDimensions here rather than
  // the existing Dimensions-listener `viewportWidth` declared later
  // in the body — that one isn't in scope yet at this point.
  const { width: windowWidth } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  const isWebDesktop = isWeb && windowWidth >= 768;
  const isWebMobile = isWeb && windowWidth < 768;

  const status = useAppSelector(selectSegmentStatus);
  const scanItems = useAppSelector(selectScanItems);
  const masks = useAppSelector(selectMasks);
  const maskErrors = useAppSelector(selectMaskErrors);
  const selectedSet = useAppSelector(selectSelectedSet);
  const sceneCtx = useAppSelector(selectSceneContext);
  const confirmedAcceptedIds = useAppSelector(selectConfirmedAcceptedIds);
  const primaryPhotos = useAppSelector(selectPrimaryPhotos);
  const editingItemId = useAppSelector(
    (s) => selectSegment(s).editing_item_id,
  );
  const errorMsg = useAppSelector(selectSegmentError);

  // Live GPS capture for the in-scan HUD chip. Stamped the moment a
  // photo is picked / taken. Drives the top-bar "📍 lat, lng" pill
  // so the user sees the location their scan is being tagged with.
  // Also passed through to runSave so confirm/batch stamps the
  // location on the new inventory rows.
  const [scanGps, setScanGps] = useState<ScanLocation | null>(null);

  // FE-214 — `itemSerials` state + handleSerialSave/Clear handlers +
  // renderSerialChipForItem removed. The on-scan serial capture flow
  // was retired with BE-202; the carousel's `renderItemFooter` slot
  // stays in place for future plug-ins.

  const [selected, setSelected] = useState<SelectedImage | null>(null);
  // #170 — staged ShareSheet plan (lazy, Q1). null = share nothing (save to
  // Personal only). On Save we resolve any "share with someone new" groups
  // (createPendingGroups) then thread per-item group_ids into confirm/batch
  // `extras`. Accept/verify on the cards stays separate (inventory only).
  const [sharePlan, setSharePlan] = useState<SharePlan | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editBbox, setEditBbox] = useState<Bbox | null>(null);
  const [editDirty, setEditDirty] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [metaEditItemId, setMetaEditItemId] = useState<number | null>(null);
  const [refineMode, setRefineMode] = useState<RefineMode>('box');
  const [points, setPoints] = useState<AnnotationPoint[]>([]);
  const [nextPointLabel, setNextPointLabel] = useState<0 | 1>(1);
  const [drawingNew, setDrawingNew] = useState(false);
  const [draftBbox, setDraftBbox] = useState<Bbox | null>(null);
  const [draftMeta, setDraftMeta] = useState<{
    draw_duration_ms: number;
    device_kind: 'mouse' | 'touch' | 'pen';
  } | null>(null);
  const [adding, setAdding] = useState(false);
  /** AI tap-add in-flight records (#411). One per concurrent tap so
   *  the user can fire off several detection rounds while earlier
   *  ones are still resolving. Local React state — purely UI; the
   *  resolved items land in the slice via `itemStreamed`. */
  const [tapAddInflight, setTapAddInflight] = useState<TapAddRecord[]>([]);
  // Pinch-zoom: ref kept so we can still imperatively reset on
  // session reset (and a future Fit button if we re-add one).
  // Double-tap on the image is the user-facing reset path today.
  const zoomStageRef = useRef<ZoomableStageHandle | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  // Confirm is now fire-and-forget after Save (optimistic flow);
  // no need for a "Saving…" label since the user navigates away
  // immediately. State retained briefly via a ref-only flag if we
  // ever need to gate other affordances during the in-flight call.
  // Viewport width for carousel page sizing. We re-measure on
  // orientation changes via the Dimensions listener.
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    Dimensions.get('window').width,
  );
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => {
      setViewportWidth(window.width);
    });
    return () => sub?.remove?.();
  }, []);

  // Currently-focused bbox — tap on the stage to set, tap again
  // to clear. The focused item's card pins to the top of the rail
  // and the per-focus action strip (Select/Refine/Delete) anchors
  // to its bbox.
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);
  // When the user taps "Annotate" on an unmasked rail card, we
  // enter draw-for-this-item mode. On commit, we POST /resegment
  // with use_box_as_mask=true against this id.
  const [annotatingItemId, setAnnotatingItemId] = useState<number | null>(
    null,
  );
  // Legacy brief-flash hook — kept for the rail's `highlighted`
  // prop so the card border still pulses on selection toggles.
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(
    null,
  );
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drawEnteredAtRef = useRef<number | null>(null);
  const cancelCountRef = useRef<number>(0);
  const abortRef = useRef<AbortController | null>(null);

  // FE #124.6 + #124.7 — resume / retry from Scan History.
  // Route params shape (set by ScanHistoryScreen + DashboardScreen
  // banner): `{ file_record_id?: number, resume?: boolean }`.
  //
  // When present, we skip the fresh-upload path entirely and:
  //   (a) `GET /users/scans/{id}` to read status + detected items +
  //       source photo URL,
  //   (b) dispatch `seedFromBackend` to hydrate segmentSlice with
  //       the items + bboxes the server already has,
  //   (c) synthesize a `SelectedImage` from the BE's `image_url` so
  //       the screen flips out of the `pick` phase into `review`,
  //   (d) for IN_PROGRESS: open the resume SSE via
  //       `streamSegmentResume(file_record_id)` and consume the
  //       remaining events (server picks up where it left off).
  //   (e) for COMPLETED: auto-redirect to SceneViewer (nothing to
  //       resume here).
  //   (f) for FAILED / CANCELLED: leave the banner with a Retry CTA
  //       that calls `streamSegmentResume` (backend#313 made the
  //       endpoint idempotent — server clears pre-confirm
  //       inventory_items + replays the pipeline).
  //
  // Guarded with a ref so the fetch only fires ONCE per mount even
  // if the route params re-emit (React Navigation re-renders).
  const route = useRoute<any>();
  // React Navigation's linking parser leaves URL query params as
  // strings ("1056"), so coerce + Number-check defensively so this
  // works for both in-app navigation (Number) AND deep-link URLs
  // (String). Same for `resume=true` (string "true" or boolean).
  const rawFileRecordId = (route?.params as any)?.file_record_id;
  const resumeFileRecordId: number | undefined = (() => {
    if (typeof rawFileRecordId === 'number' && Number.isFinite(rawFileRecordId))
      return rawFileRecordId;
    if (typeof rawFileRecordId === 'string') {
      const n = Number(rawFileRecordId);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  })();
  const rawResume = (route?.params as any)?.resume;
  const isResumeRequest: boolean =
    (rawResume === true ||
      rawResume === 'true' ||
      rawResume === '1' ||
      rawResume === 1) &&
    typeof resumeFileRecordId === 'number';
  const resumeHandledRef = useRef<number | null>(null);
  // Banner state — fetched on mount when isResumeRequest is true.
  const [resumeBanner, setResumeBanner] = useState<{
    status: 'IN_PROGRESS' | 'READY_TO_SAVE' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    detected_count: number;
    saved_count: number;
    room_context: string | null;
    filename: string | null;
    fileRecordId: number;
  } | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resumeRetrying, setResumeRetrying] = useState(false);

  /** Consume an SSE iterator and dispatch the standard Redux events.
   *  Shared between the resume effect (IN_PROGRESS auto-attach) and
   *  the manual [Retry] CTA on FAILED / CANCELLED. Mirrors the
   *  switch-case body in `runScan` — kept minimal here (we don't
   *  re-process `pipeline_switched` toasts on resume since they're
   *  noise that already showed up the first time). */
  const consumeResumeStream = useCallback(
    async (
      iter: AsyncGenerator<any, void, void>,
      t0: number,
    ): Promise<void> => {
      for await (const evt of iter) {
        dispatch(
          logEvent({
            ts_ms: performance.now() - t0,
            // `source: 'scan'` (not 'resume') because logEvent's
            // union only knows about the fresh-scan + primary-photo
            // sources today. Extending the union is a separate
            // perf-telemetry change; for now we tag resume events
            // as 'scan' since they share the same SSE shape.
            source: 'scan',
            event: evt.type,
            ok:
              evt.type !== 'error' &&
              evt.type !== 'item_error' &&
              evt.type !== 'mask_error',
          }),
        );
        switch (evt.type) {
          case 'status':
            dispatch(scanStatus({ file_record_id: evt.data.file_record_id }));
            break;
          case 'item':
            dispatch(itemStreamed({ item: evt.data, t: performance.now() }));
            break;
          case 'segmentation_started':
            dispatch(
              segmentationStarted({
                image_width_px: evt.data.image_width_px,
                image_height_px: evt.data.image_height_px,
              }),
            );
            break;
          case 'mask':
            dispatch(
              maskStreamed({
                mask: evt.data as MaskRecord,
                t: performance.now(),
              }),
            );
            break;
          case 'mask_error':
            dispatch(maskErrored(evt.data));
            break;
          case 'summary':
            dispatch(summaryReceived(evt.data));
            break;
          case 'segmentation_complete':
            dispatch(segmentationComplete());
            break;
          case 'complete':
            dispatch(streamCompleted({ t: performance.now() }));
            break;
          case 'error':
            dispatch(streamErrored(evt.data));
            break;
        }
      }
    },
    [dispatch],
  );

  /** Manual [Retry] handler — re-arm `runResumeStream` for a
   *  FAILED/CANCELLED scan. The banner's [Retry] CTA fires this. */
  const handleResumeRetry = useCallback(async () => {
    if (!resumeBanner) return;
    setResumeRetrying(true);
    setResumeError(null);
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    const t0 = performance.now();
    try {
      const iter = streamSegmentResume(resumeBanner.fileRecordId, {
        signal: controller.signal,
      });
      await consumeResumeStream(iter as any, t0);
      // After a successful replay the banner is no longer relevant —
      // the segmentSlice's `streamCompleted` flipped status to
      // 'complete' and the picker is hidden behind the review stage.
      setResumeBanner(null);
    } catch (err: any) {
      setResumeError(
        err?.response?.data?.detail ??
          err?.message ??
          'Retry failed — try again from Scan history',
      );
    } finally {
      setResumeRetrying(false);
    }
  }, [resumeBanner, consumeResumeStream]);

  useEffect(() => {
    if (!isResumeRequest || resumeFileRecordId == null) return;
    if (resumeHandledRef.current === resumeFileRecordId) return;
    resumeHandledRef.current = resumeFileRecordId;
    setResumeLoading(true);
    setResumeError(null);
    (async () => {
      try {
        const detail: any = await api.getUserScan(resumeFileRecordId);
        // BE response may use legacy shape on older accounts — infer
        // the status the same way scansSlice's normalizer does.
        const verifiedCount = detail.verified_count ?? detail.saved_count ?? 0;
        const itemsCount = detail.items_count ?? detail.detected_count ?? 0;
        const bboxCount = detail.bbox_count ?? 0;
        let inferredStatus:
          | 'IN_PROGRESS'
          | 'READY_TO_SAVE'
          | 'COMPLETED'
          | 'FAILED'
          | 'CANCELLED' = 'FAILED';
        if (verifiedCount > 0) inferredStatus = 'COMPLETED';
        else if (bboxCount > 0 || itemsCount > 0) inferredStatus = 'READY_TO_SAVE';
        const status = (detail.status as any) ?? inferredStatus;

        setResumeBanner({
          status,
          detected_count: detail.detected_count ?? itemsCount,
          saved_count: detail.saved_count ?? verifiedCount,
          room_context: detail.room_context ?? null,
          filename: detail.filename ?? null,
          fileRecordId: resumeFileRecordId,
        });

        // COMPLETED is handled by a separate effect that redirects
        // to SceneViewer; we still seed the banner above so the
        // navigation animation has a beat of context.
        if (status === 'COMPLETED') return;

        // Seed Redux with the BE's view of the scan + flip the
        // screen into the `review` phase by synthesizing a
        // SelectedImage from the source image URL. The synthesized
        // image is web-only — Resume is a web-only feature today
        // (matches streamScan's Platform.OS === 'web' guard).
        const items: any[] = Array.isArray(detail.items) ? detail.items : [];
        dispatch(
          seedFromBackend({
            file_record_id: resumeFileRecordId,
            storage_url: detail.image_url ?? null,
            ai_description:
              detail?.image_analysis?.ai_description ??
              detail.ai_description ??
              null,
            room_context: detail.room_context ?? null,
            items,
            status:
              status === 'COMPLETED'
                ? 'READY_TO_SAVE' // shouldn't reach here; guard above
                : (status as
                    | 'IN_PROGRESS'
                    | 'READY_TO_SAVE'
                    | 'FAILED'
                    | 'CANCELLED'),
          }),
        );
        if (detail.image_url) {
          setSelected({
            uri: detail.image_url,
            name: detail.filename ?? 'resumed-scan.jpg',
            type: 'image/jpeg',
          });
        }

        // #161 — For READY_TO_SAVE the review stage IS the save surface:
        // the image + bboxes + per-item rows + the bottom [Save N] CTA
        // all render once we've seeded + set `selected` above. The top
        // "Ready to save · N not saved yet · [Open]" banner duplicates
        // that exact affordance, so drop it. We keep the banner for
        // IN_PROGRESS (live-watch context), FAILED, and CANCELLED, where
        // it carries info the review stage can't. Cleared in the same
        // async tick as the seed, so the banner never paints for this
        // status (React batches the setState calls before re-render).
        if (status === 'READY_TO_SAVE' && detail.image_url) {
          setResumeBanner(null);
        }

        // IN_PROGRESS — re-attach the SSE so any items the server
        // hasn't streamed yet land in Redux as they arrive.
        if (status === 'IN_PROGRESS') {
          const controller = new AbortController();
          abortRef.current?.abort();
          abortRef.current = controller;
          const t0 = performance.now();
          try {
            const iter = streamSegmentResume(resumeFileRecordId, {
              signal: controller.signal,
            });
            await consumeResumeStream(iter as any, t0);
          } catch (streamErr: any) {
            // Don't blow away the seeded review state — surface the
            // error in the banner and let the user retry manually.
            setResumeError(
              streamErr?.response?.data?.detail ??
                streamErr?.message ??
                'Live re-attach failed — the items we already have are still listed',
            );
          }
        }
      } catch (err: any) {
        setResumeError(
          err?.response?.data?.detail ??
            err?.message ??
            'Couldn’t load this scan',
        );
      } finally {
        setResumeLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResumeRequest, resumeFileRecordId]);

  // COMPLETED scans bypass this screen entirely — there's nothing to
  // resume, the items are already saved. Route the user straight to
  // the SceneViewer so they land on the room photo + bbox overlay
  // for the saved scene rather than a half-useful "pick another
  // photo" screen.
  useEffect(() => {
    if (!resumeBanner) return;
    if (resumeBanner.status !== 'COMPLETED') return;
    navigation.navigate('SceneViewer' as never, {
      fileId: resumeBanner.fileRecordId,
    } as never);
  }, [resumeBanner, navigation]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      // Deliberately NOT cancelling the photo pool here — it lives
      // in a module-scoped service so post-confirm streams can keep
      // running even after the user navigates away from this
      // screen (Items grid catches the photos via BrowseSlice).
      dispatch(resetSession());
    },
    [dispatch],
  );

  const handleToggleSelected = useCallback(
    (id: number) => {
      dispatch(toggleSelected(id));
      // Glow the matching rail card — subtle attention beacon.
      setHighlightedItemId(id);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(
        () => setHighlightedItemId(null),
        900,
      );
    },
    [dispatch],
  );

  // Tap-to-focus with cycle-outward semantics for nested bboxes.
  //
  //   first tap on id X       → focus X
  //   tap a different id Y    → focus Y
  //   tap the focused X again → find the smallest bbox that
  //                              STRICTLY CONTAINS X's center and
  //                              is LARGER than X; focus that
  //                              (Photoshop alt-click / Figma
  //                              deep-select, adapted for touch)
  //   tap focused X at the outermost (nothing contains it) → defocus
  //
  // Reads from `annotationItems` which only holds items whose
  // masks arrived, so the cycle never chases missing geometry.
  const handleFocusBbox = useCallback(
    (id: number) => {
      setFocusedItemId((prev) => {
        if (prev !== id) return id;
        // Cycle outward: find the smallest mask that STRICTLY
        // CONTAINS the current's center and is LARGER than it.
        // Reads from the raw masks record rather than the derived
        // annotationItems so we don't depend on the useMemo's
        // lexical position in the render.
        const current = masks[prev];
        if (!current) return null;
        const cx = current.bbox.x + current.bbox.w / 2;
        const cy = current.bbox.y + current.bbox.h / 2;
        const currentArea = current.bbox.w * current.bbox.h;
        let best: { id: number; area: number } | null = null;
        for (const key of Object.keys(masks)) {
          const mid = Number(key);
          if (mid === prev) continue;
          const m = masks[mid];
          if (!m) continue;
          const area = m.bbox.w * m.bbox.h;
          if (area <= currentArea) continue;
          const inside =
            cx >= m.bbox.x &&
            cx <= m.bbox.x + m.bbox.w &&
            cy >= m.bbox.y &&
            cy <= m.bbox.y + m.bbox.h;
          if (!inside) continue;
          if (best === null || area < best.area) {
            best = { id: mid, area };
          }
        }
        return best ? best.id : null;
      });
    },
    [masks],
  );

  const handleEnterRefine = useCallback(
    (id: number) => {
      const mask = masks[id];
      if (!mask) {
        Toast.show({ type: 'info', text1: 'Waiting for mask…' });
        return;
      }
      // Refine and draw are mutually exclusive. If the user enters
      // refine from the rail while a draw or annotate session is
      // still open, clear everything first — otherwise cancelling
      // refine would silently drop them back into the other mode.
      setDrawingNew(false);
      setAnnotatingItemId(null);
      setDraftBbox(null);
      setDraftMeta(null);
      drawEnteredAtRef.current = null;
      // Clear focus — refine takes over the visual frame.
      setFocusedItemId(null);
      setEditBbox(mask.bbox);
      setEditDirty(false);
      setRefineMode('box');
      setPoints([]);
      setNextPointLabel(1);
      // Collapse the sheet so the image is fullscreen for refine.
      dispatch(enterRefine(id));
    },
    [masks, dispatch],
  );

  const handleCancelRefine = useCallback(() => {
    setEditBbox(null);
    setEditDirty(false);
    setPoints([]);
    setRefineMode('box');
    dispatch(exitRefine());
  }, [dispatch]);

  const handleAddPoint = useCallback((p: AnnotationPoint) => {
    setPoints((prev) => {
      if (prev.length >= POINT_CAP) return prev;
      return [...prev, p];
    });
  }, []);

  const handleClearPoints = useCallback(() => setPoints([]), []);

  const handleApplyRefine = useCallback(async () => {
    const id = editingItemId;
    if (typeof id !== 'number' || !editBbox) return;
    setApplyBusy(true);
    try {
      const bboxInt = {
        x: Math.round(editBbox.x),
        y: Math.round(editBbox.y),
        w: Math.round(editBbox.w),
        h: Math.round(editBbox.h),
      };
      // Adaptive payload per PRD §8.8:
      //   - points present → SAM3 smart-refit (use_box_as_mask: false)
      //   - otherwise       → user's exact box (use_box_as_mask: true)
      const hasPoints = refineMode === 'points' && points.length > 0;
      const res = await api.resegmentInventoryItem(id, {
        bbox: bboxInt,
        ...(hasPoints
          ? { points, use_box_as_mask: false }
          : { use_box_as_mask: true }),
      });
      const newMask: MaskRecord = {
        inventory_item_id: id,
        bbox: res.bbox,
        mask_url: res.mask_url,
        confidence: res.confidence,
        model: res.segmentation_model,
        label_used: masks[id]?.label_used,
        iou_original_vs_new: res.iou_original_vs_new,
        area_ratio_new_over_original: res.area_ratio_new_over_original,
        centroid_shift_px: res.centroid_shift_px,
        _corrected: true,
      };
      dispatch(maskStreamed({ mask: newMask, t: performance.now() }));
      Toast.show({
        type: 'success',
        text1: hasPoints ? 'Smart refit applied' : 'Box saved',
        text2: `IoU ${res.iou_original_vs_new.toFixed(2)} · ${res.segmentation_model}`,
      });
      setEditBbox(null);
      setEditDirty(false);
      setPoints([]);
      setRefineMode('box');
      dispatch(exitRefine());
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Could not save box';
      Toast.show({
        type: 'error',
        text1: 'Failed to save',
        text2: String(detail),
      });
    } finally {
      setApplyBusy(false);
    }
  }, [dispatch, editBbox, editingItemId, masks, points, refineMode]);

  const handleEditDetails = useCallback((id: number) => {
    setMetaEditItemId(id);
  }, []);

  const metaEditItem: InventoryItemResponse | null = metaEditItemId
    ? scanItems.find((it) => it.id === metaEditItemId) ?? null
    : null;

  const handleMetaSaved = useCallback(
    (updated: InventoryItemResponse) => {
      // Patch the item in-place in the slice via the itemStreamed
      // reducer (it replaces-on-known-id without reordering).
      dispatch(
        itemStreamed({ item: updated, t: performance.now() }),
      );
      setMetaEditItemId(null);
    },
    [dispatch],
  );

  // ── Manual add: user draws a bbox, names the item, we POST to
  //    /inventory/manual.

  const handleEnterDraw = useCallback(() => {
    if (editingItemId !== null) return;
    drawEnteredAtRef.current = performance.now();
    cancelCountRef.current = 0;
    setDraftBbox(null);
    setDraftMeta(null);
    setDrawingNew(true);
  }, [editingItemId]);

  const handleExitDraw = useCallback(() => {
    drawEnteredAtRef.current = null;
    setDrawingNew(false);
    setDraftBbox(null);
    setDraftMeta(null);
  }, []);

  const handleDraftCommit = useCallback(
    (
      bbox: Bbox,
      meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
    ) => {
      setDraftBbox(bbox);
      setDraftMeta(meta);
    },
    [],
  );

  const handleDraftCancel = useCallback(
    (reason: 'too-small' | 'escape' | 'pointer-cancel') => {
      // Only "too-small" counts as a retry — the user was trying
      // to draw and released too early. Escape / pointercancel are
      // intentional exits; mixing them in noises the feedback row.
      // With AI tap-add wired (#411), too-small gestures under the
      // tap threshold no longer reach this callback — they route to
      // onTap. So "too-small" here only fires for slow micro-drags.
      if (reason === 'too-small') cancelCountRef.current += 1;
    },
    [],
  );

  // ── AI tap-add (#411): fire `POST /inventory/manual-analyze`
  //    with an image-pixel bbox, render a ghost rect at the bbox
  //    location while the BE works, and morph the ghost into a
  //    success / softfail / harderror state on response.

  /** Remove a ghost record from the list. Called by the auto-dismiss
   *  timer inside TapAddGhost and by the user's close-X on errors. */
  const handleTapAddDismiss = useCallback((id: string) => {
    setTapAddInflight((prev) => prev.filter((r) => r.id !== id));
  }, []);

  /** Fire one AI-tap-add round. Used both from `onTap` (single tap →
   *  manufactured 12% square) and from `onCommit` (drag → custom
   *  rect). Skips the legacy NameDraftItemSheet entirely; the BE
   *  fills the row's name itself. */
  const runTapAddAnalyze = useCallback(
    async (
      bbox: Bbox,
      meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
    ) => {
      const fileRecordId = sceneCtx.file_record_id;
      if (!fileRecordId) {
        Toast.show({
          type: 'error',
          text1: 'Can’t add yet',
          text2: 'Wait for the initial scan to finish.',
        });
        return;
      }
      // Round to ints — BE rejects floats (422).
      const bboxInt = {
        x: Math.round(bbox.x),
        y: Math.round(bbox.y),
        w: Math.round(bbox.w),
        h: Math.round(bbox.h),
      };
      const id = `tap-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      const startedAt = performance.now();
      const hesitation =
        drawEnteredAtRef.current !== null
          ? Math.max(
              0,
              Math.round(performance.now() - drawEnteredAtRef.current),
            )
          : undefined;
      // Insert pending ghost so the user sees feedback immediately.
      setTapAddInflight((prev) => [
        ...prev,
        { id, bbox: bboxInt, startedAt, status: 'pending' },
      ]);
      // Exit draw mode synchronously so the user can tap a SECOND
      // object without re-toggling the + button.
      handleExitDraw();

      try {
        const res = await api.manualAnalyze({
          file_record_id: fileRecordId,
          bbox: bboxInt,
          interaction: {
            device_kind: meta.device_kind,
            draw_duration_ms: meta.draw_duration_ms,
            hesitation_ms: hesitation,
            cancel_count: cancelCountRef.current,
          },
        });
        const item = res.item;
        // Seed the item + its rect mask into the slice — same path
        // the SSE stream uses, so all downstream surfaces (rail,
        // bbox layer, mask layer) pick it up automatically.
        dispatch(itemStreamed({ item, t: performance.now() }));
        if (typeof item.id === 'number' && item.bbox && item.mask_url) {
          dispatch(
            maskStreamed({
              mask: {
                inventory_item_id: item.id,
                bbox: item.bbox,
                mask_url: item.mask_url,
                confidence:
                  typeof item.segmentation_confidence === 'number'
                    ? item.segmentation_confidence
                    : 1,
                model: item.segmentation_model ?? 'user_box',
                label_used: item.item_name,
              } as MaskRecord,
              t: performance.now(),
            }),
          );
        }
        // Update the ghost. error != null = soft-failure (row is
        // real but AI didn't fill it). UX: orange ghost, parent
        // opens rename sheet via metaEditItemId.
        const isSoftFail = typeof res.error === 'string' && res.error.length > 0;
        setTapAddInflight((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: isSoftFail ? 'softfail' : 'success',
                  resolvedAt: performance.now(),
                  latencyMs: res.latency_ms,
                  itemName: item.item_name,
                  errorMessage: isSoftFail ? res.error ?? undefined : undefined,
                }
              : r,
          ),
        );
        if (isSoftFail && typeof item.id === 'number') {
          // Open the existing meta-edit sheet on the placeholder
          // row so the user can name it immediately.
          setMetaEditItemId(item.id);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail || err?.message || 'Could not add item';
        // Friendlier copy for the common 4xx cases.
        let friendly = String(detail);
        if (status === 429) friendly = 'Slow down — limit hit. Try again in a minute.';
        else if (status === 403) friendly = 'Can’t access this photo.';
        else if (status === 404) friendly = 'This photo was deleted.';
        else if (status === 422) friendly = 'Bbox math is off — try again.';
        setTapAddInflight((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: 'harderror',
                  resolvedAt: performance.now(),
                  errorMessage: friendly,
                }
              : r,
          ),
        );
      }
    },
    [dispatch, handleExitDraw, sceneCtx.file_record_id],
  );

  // Note: `onTap` is wired inline at the DrawNewItemOverlay mount
  // site since `transform.imageSize` only exists inside the
  // ImageAnnotationStage render-prop scope.

  /** Retry handler for the harderror ghost — re-fires the same bbox. */
  const handleTapAddRetry = useCallback(
    (record: TapAddRecord) => {
      handleTapAddDismiss(record.id);
      // Replay with the same bbox + fresh telemetry; device_kind
      // is best-effort since we no longer have the original event.
      runTapAddAnalyze(record.bbox, {
        draw_duration_ms: 0,
        device_kind: 'mouse',
      });
    },
    [handleTapAddDismiss, runTapAddAnalyze],
  );

  // Annotate an existing (unmasked) item. The user taps "Annotate"
  // on a rail card; we enter a draw mode scoped to that item id.
  // On commit we POST /inventory/{id}/resegment with
  // use_box_as_mask=true — server rasterises the rect to a mask.
  const handleEnterAnnotate = useCallback((id: number) => {
    setAnnotatingItemId(id);
    drawEnteredAtRef.current = performance.now();
    cancelCountRef.current = 0;
    setDraftBbox(null);
    setDraftMeta(null);
  }, []);

  const handleExitAnnotate = useCallback(() => {
    drawEnteredAtRef.current = null;
    setAnnotatingItemId(null);
    setDraftBbox(null);
    setDraftMeta(null);
  }, []);

  const handleAnnotateCommit = useCallback(
    async (
      bbox: Bbox,
      meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
    ) => {
      const id = annotatingItemId;
      if (id === null) return;
      // Deactivate the draw overlay synchronously — otherwise a
      // second pointerdown during the in-flight API call would
      // fire another onCommit.
      setAnnotatingItemId(null);
      try {
        const bboxInt = {
          x: Math.round(bbox.x),
          y: Math.round(bbox.y),
          w: Math.round(bbox.w),
          h: Math.round(bbox.h),
        };
        const hesitation =
          drawEnteredAtRef.current !== null
            ? Math.max(
                0,
                Math.round(performance.now() - drawEnteredAtRef.current),
              )
            : undefined;
        const res = await api.resegmentInventoryItem(id, {
          bbox: bboxInt,
          use_box_as_mask: true,
          interaction: {
            device_kind: meta.device_kind,
            draw_duration_ms: meta.draw_duration_ms,
            hesitation_ms: hesitation,
            cancel_count: cancelCountRef.current,
          },
        });
        dispatch(
          maskStreamed({
            mask: {
              inventory_item_id: id,
              bbox: res.bbox,
              mask_url: res.mask_url,
              confidence: res.confidence,
              model: res.segmentation_model,
              label_used: masks[id]?.label_used,
            } as MaskRecord,
            t: performance.now(),
          }),
        );
        Toast.show({ type: 'success', text1: 'Annotation saved' });
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || 'Could not save';
        Toast.show({
          type: 'error',
          text1: 'Failed to annotate',
          text2: String(detail),
        });
      }
    },
    [annotatingItemId, dispatch, masks],
  );

  const handleDraftSave = useCallback(
    async (values: NameDraftValues) => {
      const fileRecordId = sceneCtx.file_record_id;
      if (!draftBbox || !fileRecordId) {
        Toast.show({
          type: 'error',
          text1: 'Can\u2019t add yet',
          text2: 'Wait for the initial scan to finish.',
        });
        return;
      }
      setAdding(true);
      try {
        const bboxInt = {
          x: Math.round(draftBbox.x),
          y: Math.round(draftBbox.y),
          w: Math.round(draftBbox.w),
          h: Math.round(draftBbox.h),
        };
        const hesitation =
          drawEnteredAtRef.current !== null
            ? Math.max(
                0,
                Math.round(performance.now() - drawEnteredAtRef.current),
              )
            : undefined;
        const interaction: AnnotationInteraction = {
          device_kind: draftMeta?.device_kind,
          draw_duration_ms: draftMeta?.draw_duration_ms ?? null,
          hesitation_ms: hesitation,
          cancel_count: cancelCountRef.current,
        };
        const res = await api.manualAddInventoryItem({
          file_record_id: fileRecordId,
          item_name: values.item_name,
          item_category: values.item_category,
          estimated_value: values.estimated_value,
          bbox: bboxInt,
          interaction,
        });
        const item = res.inventory_item;
        dispatch(itemStreamed({ item, t: performance.now() }));
        // Server returns the item with its mask embedded — seed a
        // MaskRecord so the stage can render immediately instead
        // of waiting for a non-existent SAM3 event.
        if (
          typeof item.id === 'number' &&
          item.bbox &&
          item.mask_url
        ) {
          // Manual-add items have no original SAM3 mask to compare
          // against, so `_corrected` stays false — the IoU pill
          // would be meaningless here. The `user_box` model string
          // is enough signal on its own for downstream badges.
          dispatch(
            maskStreamed({
              mask: {
                inventory_item_id: item.id,
                bbox: item.bbox,
                mask_url: item.mask_url,
                confidence:
                  typeof item.segmentation_confidence === 'number'
                    ? item.segmentation_confidence
                    : 1,
                model: item.segmentation_model ?? 'user_box',
                label_used: item.item_name,
              } as MaskRecord,
              t: performance.now(),
            }),
          );
        }
        Toast.show({
          type: 'success',
          text1: 'Item added',
          text2: item.item_name || 'Saved to your inventory.',
        });
        handleExitDraw();
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || 'Could not add item';
        Toast.show({
          type: 'error',
          text1: 'Failed to add',
          text2: String(detail),
        });
      } finally {
        setAdding(false);
      }
    },
    [dispatch, draftBbox, draftMeta, handleExitDraw, sceneCtx.file_record_id],
  );

  // ── Delete with feedback: DELETE /inventory/{id} writes an
  //    annotation_feedback row server-side BEFORE soft-delete.

  const confirmDelete = useCallback(
    (id: number, name: string): Promise<boolean> => {
      return new Promise((resolve) => {
        if (Platform.OS === 'web') {
          // RN Alert is a no-op on web — fall back to window.confirm.
          // eslint-disable-next-line no-alert
          resolve(
            typeof window !== 'undefined'
              ? window.confirm(
                  `Delete \u201C${name || 'this item'}\u201D? This can\u2019t be undone.`,
                )
              : true,
          );
          return;
        }
        Alert.alert(
          'Delete item',
          `Remove \u201C${name || 'this item'}\u201D from the scan?`,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            {
              text: 'Delete',
              style: 'destructive',
              onPress: () => resolve(true),
            },
          ],
          { cancelable: true },
        );
      });
    },
    [],
  );

  const handleDeleteItem = useCallback(
    async (id: number) => {
      const existing = scanItems.find((it) => it.id === id);
      const nameForPrompt = existing?.item_name || '';
      const t0 = performance.now();
      const confirmed = await confirmDelete(id, nameForPrompt);
      if (!confirmed) return;
      try {
        await api.deleteInventoryItemWithFeedback(id, {
          interaction: {
            device_kind: 'mouse',
            hesitation_ms: Math.max(0, Math.round(performance.now() - t0)),
          },
        });
        dispatch(itemRemoved(id));
        // Cascade-clear any local state pinned to this id. BboxLayer
        // filters items down to focusedItemId when set, so a stale
        // pointer to a now-deleted item would render zero boxes — i.e.
        // the entire annotation overlay would vanish. Same risk
        // applies to metaEditItemId (EditItemSheet) and annotatingItemId.
        setFocusedItemId((prev) => (prev === id ? null : prev));
        setAnnotatingItemId((prev) => (prev === id ? null : prev));
        setMetaEditItemId((prev) => (prev === id ? null : prev));
        Toast.show({ type: 'success', text1: 'Item removed' });
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || 'Could not delete';
        Toast.show({
          type: 'error',
          text1: 'Failed to delete',
          text2: String(detail),
        });
      }
    },
    [confirmDelete, dispatch, scanItems],
  );

  // ── Optimistic Save ────────────────────────────────────────────
  // The items already exist in the user's inventory the moment the
  // scan stream persisted them server-side. Confirm-batch is just
  // a polish step (mark verified, queue primary-photo jobs, write
  // annotation_feedback for rejects). So the UI does NOT wait for
  // its response:
  //   1. Synchronously upsert accepted items into BrowseSlice
  //   2. Navigate to the Items grid
  //   3. Fire confirm-batch in the background; on success, spin up
  //      the photo pool (which patches BrowseSlice items in place
  //      as URLs arrive)
  //   4. On confirm failure: items still exist in the user's
  //      inventory (scan persisted them); we just don't get the
  //      polished photos. Show a soft error toast so the user knows.
  /** Internal — runs the save once the user confirms. Always saves
   *  to Personal (crew routing removed alongside SaveDestinationPicker).
   *  Builds the confirm/batch payload with optional scan_lat/lng + fires
   *  it. Photo-polish happens in the background after navigation. */
  const runSave = useCallback(
    (args: {
      fileRecordId: number;
      acceptedIds: number[];
      rejectedIds: number[];
      acceptedItems: InventoryItemResponse[];
      location: ScanLocation | null;
      /** #170 — precomputed per-item group bindings for confirm/batch's
       *  `extras`. handleSave builds this from the staged ShareSheet plan
       *  (after resolving any "share with someone new" groups). Empty /
       *  undefined = Personal-only save (no extras). */
      extras?: { item_id: number; group_ids: number[] }[];
      /** Counts for the success toast. */
      sharedItems: number;
      sharedGroups: number;
    }) => {
      const {
        fileRecordId, acceptedIds, rejectedIds, acceptedItems, location,
        extras, sharedItems, sharedGroups,
      } = args;

      // Step 1 — upsert into BrowseSlice synchronously.
      for (const item of acceptedItems) {
        dispatch(upsertInventoryItem({
          ...item, is_verified: true, requires_user_verification: false,
        }));
      }

      // Step 2 — flag slice 'confirming'.
      dispatch(confirmStarted());

      // Step 3 — kick to Items grid. Toast adapts to destinations.
      mark(TimingMark.DONE);
      reportTimingSnapshot('save', {
        accepted: acceptedIds.length, rejected: rejectedIds.length,
      });
      Toast.show({
        type: 'success',
        text1: `Saved ${acceptedIds.length} item${acceptedIds.length === 1 ? '' : 's'}`,
        text2:
          sharedGroups > 0 && sharedItems > 0
            ? `Shared ${sharedItems} item${sharedItems === 1 ? '' : 's'} with ${sharedGroups} circle${sharedGroups === 1 ? '' : 's'}. Polishing photos\u2026`
            : 'Polishing photos in the background\u2026',
      });
      navigation.navigate('MainTabs', { screen: 'Browse' } as any);

      // Step 4 — fire confirm-batch with GPS. No `extras` because we
      // no longer route to crews here. Reintroducing crew routing =
      // restore the picker + per-item extras shape.
      const payload: any = {
        file_record_id: fileRecordId,
        accepted_item_ids: acceptedIds,
        rejected_item_ids: rejectedIds,
      };
      if (location) {
        payload.scan_lat = location.lat;
        payload.scan_lng = location.lng;
      }
      // #170 — per-item group bindings (item_id → group_ids), prebuilt by
      // handleSave from the ShareSheet plan. Items not in `extras` save to
      // Personal only. Empty = nothing shared.
      if (extras && extras.length > 0) {
        payload.extras = extras;
      }

      api.confirmInventoryBatch(payload)
        .then((res) => {
          mark(TimingMark.CONFIRM_DONE);
          dispatch(confirmCompleted({
            accepted_ids: res.accepted_item_ids,
            rejected_ids: res.rejected_item_ids,
          }));
          startPrimaryPhotoStreams(res.accepted_item_ids, dispatch);
        })
        .catch((err: any) => {
          const detail = err?.response?.data?.detail || err?.message || 'unknown';
          dispatch(confirmFailed({ error: String(detail) }));
          Toast.show({
            type: 'error',
            text1: 'Photo polish unavailable',
            text2: 'Items saved; polished thumbnails couldn\u2019t generate.',
          });
          // eslint-disable-next-line no-console
          console.warn('[segment] confirmInventoryBatch failed', err);
        });
    },
    [dispatch, navigation],
  );

  // Cached set of items being saved — used by the picker for the
  /** Entry point — Save button. Validates, captures GPS, then saves
   *  to Personal and navigates to Items. SaveDestinationPicker was
   *  removed; if crew-routing is re-introduced later, restore the
   *  Mode B/C branch + the picker mount at the bottom of the file. */
  const handleSave = useCallback(() => {
    const fileRecordId = sceneCtx.file_record_id;
    if (!fileRecordId) {
      Toast.show({
        type: 'error',
        text1: 'Can\u2019t save yet',
        text2: 'Wait for the scan to finish.',
      });
      return;
    }
    const acceptedIds: number[] = [];
    const rejectedIds: number[] = [];
    const acceptedItems: InventoryItemResponse[] = [];
    for (const it of scanItems) {
      const id = it.id;
      if (typeof id !== 'number') continue;
      if (selectedSet.has(id)) {
        acceptedIds.push(id);
        acceptedItems.push(it);
      } else {
        rejectedIds.push(id);
      }
    }
    if (acceptedIds.length === 0) {
      Toast.show({
        type: 'info',
        text1: 'Pick at least one item to save.',
      });
      return;
    }

    // Capture GPS in parallel and save to Personal-only. The picker
    // previously offered crew routing but was removed — keeping the
    // GPS attach so confirm/batch can still stamp the scan location
    // for the new items.
    const acceptedSet = new Set(acceptedIds);
    captureScanLocation()
      .catch(() => null)
      .then(async (loc) => {
        let extras: { item_id: number; group_ids: number[] }[] | undefined;
        let sharedItems = 0;
        let sharedGroups = 0;
        const plan = sharePlan;
        if (
          plan &&
          (Object.keys(plan.selections).length > 0 || plan.pendingNew.length > 0)
        ) {
          // #170 Q1 — resolve "share with someone new" groups → real ids now.
          const { created, failures } = await createPendingGroups(
            dispatch,
            plan.pendingNew,
          );
          if (failures.length) {
            Toast.show({
              type: 'info',
              text1: 'Some circles couldn’t be created',
              text2: failures[0],
            });
          }
          // Build item_id → group_ids, restricted to still-accepted items
          // (the user may have un-accepted something after picking).
          const byItem = new Map<number, Set<number>>();
          const bind = (itemId: number, gid: number) => {
            if (!acceptedSet.has(itemId)) return;
            const s = byItem.get(itemId) ?? new Set<number>();
            s.add(gid);
            byItem.set(itemId, s);
          };
          for (const [gidStr, ids] of Object.entries(plan.selections)) {
            const gid = Number(gidStr);
            for (const id of ids) bind(id, gid);
          }
          for (const c of created) for (const id of c.itemIds) bind(id, c.groupId);

          extras = Array.from(byItem.entries())
            .filter(([, s]) => s.size > 0)
            .map(([item_id, s]) => ({ item_id, group_ids: Array.from(s) }));
          sharedItems = extras.length;
          const gset = new Set<number>();
          for (const e of extras) for (const g of e.group_ids) gset.add(g);
          sharedGroups = gset.size;
        }
        runSave({
          fileRecordId, acceptedIds, rejectedIds, acceptedItems,
          location: loc,
          extras,
          sharedItems,
          sharedGroups,
        });
      });
  }, [
    scanItems,
    selectedSet,
    sceneCtx.file_record_id,
    runSave,
    sharePlan,
    dispatch,
  ]);

  // "Done" — user is happy with what they confirmed. Navigate to
  // the Items grid so they can watch photos populate live there.
  // Pool keeps running in the background; mergeInventoryItem patches
  // the BrowseSlice items in place as URLs arrive.
  const handleFinishDone = useCallback(() => {
    navigation.navigate('MainTabs', { screen: 'Browse' } as any);
  }, [navigation]);

  // "Scan another" — fresh start. Cancel the pool so we don't keep
  // streams open for items the user has already moved on from.
  const handleScanAgainFromDone = useCallback(() => {
    cancelPrimaryPhotoStreams();
    setSelected(null);
    setEditBbox(null);
    setEditDirty(false);
    setMetaEditItemId(null);
    setDrawingNew(false);
    setAnnotatingItemId(null);
    setFocusedItemId(null);
    setDraftBbox(null);
    setDraftMeta(null);
    dispatch(resetSession());
  }, [dispatch]);

  const runScan = useCallback(
    async (image: SelectedImage) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // startMark() is anchored at the click handler (pickFromLibrary
      // / takePhoto). Here we only stamp START — the moment the SSE
      // stream is about to open. Diff (START - 0) tells us how much
      // of TTFV was OS-picker time vs our render time.
      const t0 = mark(TimingMark.START);
      dispatch(scanStarted({ t_start: t0 }));

      try {
        const iter = streamScan(
          { uri: image.uri, name: image.name, type: image.type },
          { signal: controller.signal },
        );
        for await (const evt of iter) {
          dispatch(
            logEvent({
              ts_ms: performance.now() - t0,
              source: 'scan',
              event: evt.type,
              ok:
                evt.type !== 'error' &&
                evt.type !== 'item_error' &&
                evt.type !== 'mask_error',
            }),
          );
          switch (evt.type) {
            case 'status':
              dispatch(scanStatus({ file_record_id: evt.data.file_record_id }));
              break;
            case 'pipeline_switched':
              // Non-blocking: backend swapped from the primary
              // pipeline (e.g. Gemini) to the fallback (e.g. OpenAI)
              // before any item arrived. Items still flow normally
              // on the new pipeline; just surface a soft toast so
              // the user has context if they're staring at the
              // scanning hero a moment longer than usual.
              Toast.show({
                type: 'info',
                text1: 'Switched to backup model',
                text2: `Continuing your scan${evt.data.reason ? ` (${evt.data.reason})` : ''}…`,
                visibilityTime: 2500,
              });
              break;
            case 'item':
              dispatch(
                itemStreamed({
                  item: evt.data,
                  t: mark(TimingMark.FIRST_ITEM, { onlyFirst: true }),
                }),
              );
              break;
            case 'item_error':
              break;
            case 'segmentation_started':
              dispatch(
                segmentationStarted({
                  image_width_px: evt.data.image_width_px,
                  image_height_px: evt.data.image_height_px,
                }),
              );
              break;
            case 'mask':
              // Each mask call overwrites LAST_MASK so by the end of
              // the stream it points at the most recent mask event —
              // which is exactly TTLC. FIRST_MASK uses onlyFirst so
              // it stays anchored on event #1.
              mark(TimingMark.LAST_MASK);
              dispatch(
                maskStreamed({
                  mask: evt.data as MaskRecord,
                  t: mark(TimingMark.FIRST_MASK, { onlyFirst: true }),
                }),
              );
              break;
            case 'mask_error':
              dispatch(maskErrored(evt.data));
              break;
            case 'summary':
              dispatch(summaryReceived(evt.data));
              break;
            case 'segmentation_complete':
              dispatch(segmentationComplete());
              break;
            case 'complete':
              dispatch(
                streamCompleted({ t: mark(TimingMark.SCAN_COMPLETE) }),
              );
              break;
            case 'error':
              dispatch(streamErrored(evt.data));
              break;
          }
        }
      } catch (err: any) {
        if (controller.signal.aborted) return;
        if (err instanceof ScanHttpError) {
          if (err.status === 401) {
            Toast.show({
              type: 'error',
              text1: 'Session expired',
              text2: 'Please log in again.',
            });
          } else if (err.status === 413) {
            Toast.show({ type: 'error', text1: 'That file is too large.' });
          } else {
            Toast.show({
              type: 'error',
              text1: `Upload failed (HTTP ${err.status})`,
              // err.detail can be a Pydantic array on a 422 — rendering it raw
              // throws React #31 and crashes the app; flatten to a string.
              text2: apiErrorText(err),
            });
          }
        } else {
          Toast.show({
            type: 'error',
            text1: 'Network error',
            text2: 'Check your connection and try again.',
          });
        }
        dispatch(streamErrored({ error: err?.message || 'stream failed' }));
      }
    },
    [dispatch],
  );

  const pickFromLibrary = useCallback(async () => {
    // Anchor the timing session at the click moment so TTFV / TTFOD
    // measure from the user's intent, not from when the OS picker
    // resolved. PRD §4 perceptual contract.
    startMark();
    // Kick off GPS capture in parallel with the OS picker; result
    // lands in the top-bar HUD pill as soon as the browser resolves.
    captureScanLocation().catch(() => null).then((loc) => {
      if (loc) setScanGps(loc);
    });
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (perm.status !== 'granted') {
      Alert.alert('Permission needed', 'We need photo access to scan items.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.85,
    });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    const image: SelectedImage = {
      uri: asset.uri,
      name: asset.fileName || `scan_${Date.now()}.jpg`,
      type: (asset as any).mimeType || 'image/jpeg',
      size: asset.fileSize,
    };
    setSelected(image);
    // setSelected triggers the scanning-hero render — that's TTFV.
    mark(TimingMark.FIRST_VISUAL);
    runScan(image);
  }, [runScan]);

  const takePhoto = useCallback(async () => {
    startMark();
    captureScanLocation().catch(() => null).then((loc) => {
      if (loc) setScanGps(loc);
    });
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (perm.status !== 'granted') {
      Alert.alert('Permission needed', 'Camera access is needed to scan.');
      return;
    }
    const result = await ImagePicker.launchCameraAsync({ quality: 0.85 });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    const image: SelectedImage = {
      uri: asset.uri,
      name: asset.fileName || `scan_${Date.now()}.jpg`,
      type: (asset as any).mimeType || 'image/jpeg',
      size: asset.fileSize,
    };
    setSelected(image);
    mark(TimingMark.FIRST_VISUAL);
    runScan(image);
  }, [runScan]);

  // Web-desktop drop / paste / browse — File objects in, SelectedImage
  // out, same runScan pipeline as the OS picker paths. Multi-file
  // batches scan the first only for v1.
  const handleWebFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const file = files[0];
      if (!file.type.startsWith('image/')) {
        Toast.show({
          type: 'error',
          text1: 'Only image files are supported',
          text2: file.type || 'unknown type',
        });
        return;
      }
      if (files.length > 1) {
        Toast.show({
          type: 'info',
          text1: 'Scanning the first photo',
          text2: `${files.length - 1} more queued — multi-scan coming soon.`,
        });
      }
      startMark();
      captureScanLocation().catch(() => null).then((loc) => {
        if (loc) setScanGps(loc);
      });
      const image: SelectedImage = {
        uri: URL.createObjectURL(file),
        name: file.name || `scan_${Date.now()}.jpg`,
        type: file.type || 'image/jpeg',
        size: file.size,
      };
      clarityHelpers.trackInteraction('segment_web_drop', 'drop');
      setSelected(image);
      mark(TimingMark.FIRST_VISUAL);
      runScan(image);
    },
    [runScan],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    // Aborting an in-flight scan is a "user changed their mind"
    // signal. We do NOT cancel the photo pool here — by the time
    // the pool exists, the user has already confirmed save, and
    // those streams should keep running into BrowseSlice.
    setSelected(null);
    setEditBbox(null);
    setEditDirty(false);
    setMetaEditItemId(null);
    setDrawingNew(false);
    setAnnotatingItemId(null);
    setFocusedItemId(null);
    setDraftBbox(null);
    setDraftMeta(null);
    dispatch(resetSession());
  }, [dispatch]);

  // #162 — Closing the scan screen (the X in the segment HUD) should
  // return the user to the Dashboard, NOT unwind to the photo picker.
  // `reset()` alone clears `selected`, which flips phase→'pick' and
  // re-renders the Take Photo view in place — a confusing "close" that
  // leaves the user one screen deeper than they expect. We run the same
  // cleanup (abort in-flight scan + clear session) and then pop to the
  // Home tab so the affordance reads as a true dismiss.
  const handleCloseToHome = useCallback(() => {
    reset();
    navigation.navigate('MainTabs', { screen: 'Home' } as never);
  }, [reset, navigation]);

  const annotationItems: AnnotationItem[] = useMemo(() => {
    const out: AnnotationItem[] = [];
    for (const it of scanItems) {
      const id = it.id;
      if (typeof id !== 'number') continue;
      const mask = masks[id];
      if (!mask) continue;
      const lowConfidence =
        (mask.confidence ?? 1) < 0.6 ||
        (it.requires_user_verification === true);
      out.push({
        id,
        bbox: mask.bbox,
        mask_url: mask.mask_url,
        model: mask.model,
        selected: selectedSet.has(id),
        lowConfidence,
        name: it.item_name,
      });
    }
    return out;
  }, [scanItems, masks, selectedSet]);


  const railItems: ItemRailItem[] = useMemo(
    () =>
      scanItems.map((item): ItemRailItem => {
        const id = typeof item.id === 'number' ? item.id : null;
        const mask = id !== null ? masks[id] : undefined;
        return {
          item,
          selected: id !== null && selectedSet.has(id),
          hasMask: !!mask,
          maskErrored: id !== null && !!maskErrors[id],
          corrected: !!mask?._corrected,
          iou: mask?.iou_original_vs_new,
          highlighted: id !== null && highlightedItemId === id,
        };
      }),
    [scanItems, masks, maskErrors, selectedSet, highlightedItemId],
  );

  const phase: 'pick' | 'scanning' | 'review' | 'done' = !selected
    ? 'pick'
    : status === 'done'
      ? 'done'
      : status === 'scanning'
        ? 'scanning'
        : 'review';

  // Status copy for the ScanningHero chip, driven by what we know
  // from the live SSE stream. Three stages:
  //   - Pre-first-item: "Scanning your photo…"
  //   - Items arriving: "Detecting items · N found"
  //   - Scene context arrived (a sign the stream is wrapping up):
  //     append "tagging room…" while items still trickle in.
  // Returning undefined keeps the chip on its rotating generic
  // messages, so this only kicks in once we have real signal.
  const scanStatusText: string | undefined = useMemo(() => {
    if (phase !== 'scanning') return undefined;
    const n = scanItems.length;
    if (n === 0) return 'Scanning your photo…';
    const itemsLabel = `Detecting items · ${n} found`;
    if (sceneCtx.room_context && !sceneCtx.ai_description) {
      return `${itemsLabel} · tagging room…`;
    }
    return itemsLabel;
  }, [phase, scanItems.length, sceneCtx.room_context, sceneCtx.ai_description]);

  const acceptedItemsForDone = useMemo(
    () =>
      confirmedAcceptedIds
        .map((id) => scanItems.find((it) => it.id === id))
        .filter((it): it is NonNullable<typeof it> => !!it),
    [confirmedAcceptedIds, scanItems],
  );

  const streaming =
    status === 'scanning' ||
    status === 'streaming' ||
    status === 'segmenting';

  const acceptedItemsList = useMemo(
    () =>
      scanItems.filter(
        (i) => typeof i.id === 'number' && selectedSet.has(i.id),
      ),
    [scanItems, selectedSet],
  );
  const acceptedCount = acceptedItemsList.length;
  // #170 — how many groups the staged ShareSheet plan will share to
  // (existing groups with ≥1 item + each "share with someone new" group).
  const shareGroupCount = useMemo(() => {
    if (!sharePlan) return 0;
    const existing = Object.values(sharePlan.selections).filter(
      (ids) => ids.length > 0,
    ).length;
    const pending = sharePlan.pendingNew.filter((p) => p.itemIds.length > 0).length;
    return existing + pending;
  }, [sharePlan]);

  const refining = editingItemId !== null && editBbox !== null;

  // Top bar is hidden during immersive modes (refine / draw /
  // annotate) so the stage + handles can use the full viewport.
  const immersive = refining || drawingNew || annotatingItemId !== null;

  return (
    <View style={styles.page}>
      {/* FE #124.6 + #124.7 — Resume banner. Floats above whatever
          phase is active so it stays visible during pick → scanning
          → review transitions. Renders only when the screen was
          opened with `?resume=true` route params. Status decides the
          banner tone + CTA:
              IN_PROGRESS    → live ticker, opens Scan history
              READY_TO_SAVE  → ready ticker, opens Scan history
              FAILED/CANCELLED → red banner with [Retry] CTA that
                                calls streamSegmentResume() (BE#313
                                idempotent). */}
      {(resumeLoading || resumeError || resumeBanner) && (
        <ResumeBanner
          loading={resumeLoading}
          retrying={resumeRetrying}
          error={resumeError}
          banner={resumeBanner}
          onOpenHistory={() =>
            navigation.navigate('ScanHistory' as never)
          }
          onRetry={handleResumeRetry}
          onDismiss={() => {
            setResumeBanner(null);
            setResumeError(null);
          }}
        />
      )}
      {/* #163 — Resume-in-flight gate. When the screen is opened with
          `?resume=true` (Dashboard banner / ScanHistory row), `selected`
          is null until `api.getUserScan()` resolves (~300ms), so the
          `pick` branch below would render the Take Photo / camera view
          for that beat — a confusing flash before the scan loads. While
          the resume fetch is in flight, render a neutral loading screen
          instead so the user goes straight from the tap to the scan
          stage with no camera flash in between. */}
      {isResumeRequest && resumeLoading && !selected ? (
        <>
          <View style={styles.topBar}>
            <View style={styles.topBarInner}>
              <BrandLogo variant="horizontal" height={24} tone="color" />
              <Pressable
                onPress={handleCloseToHome}
                hitSlop={8}
                style={styles.closeBtn}
                accessibilityRole="button"
                accessibilityLabel="Close and return to dashboard"
              >
                <MaterialIcons
                  name="close"
                  size={22}
                  color={fpColors.onSurfaceVariant}
                />
              </Pressable>
            </View>
          </View>
          <View style={styles.resumeLoadingFill} testID="segment-resume-loading">
            <ActivityIndicator size="large" color={fpColors.primary} />
            <Text style={styles.resumeLoadingText}>Loading your scan…</Text>
          </View>
        </>
      ) : phase === 'pick' ? (
        <>
          <View style={styles.topBar}>
            <View style={styles.topBarInner}>
              <BrandLogo variant="horizontal" height={24} tone="color" />
              <Pressable
                // #164 — raw goBack() no-ops on a cold load (refresh / pasted
                // URL): the nav stack has only this screen, so the button
                // traps the user. goBackOrHome pops if it can, else → Dashboard.
                onPress={() => goBackOrHome(navigation)}
                hitSlop={8}
                style={styles.closeBtn}
                accessibilityRole="button"
                accessibilityLabel="Back"
              >
                <MaterialIcons
                  name="arrow-back"
                  size={22}
                  color={fpColors.onSurfaceVariant}
                />
              </Pressable>
            </View>
          </View>
          {isWebDesktop ? (
            <WebDesktopDropPickView onFiles={handleWebFiles} />
          ) : isWebMobile ? (
            // Snapchat-style live camera. Falls back to the 2-button
            // WebMobilePickView when getUserMedia is denied or
            // unsupported (legacy browsers, hardened privacy modes).
            <WebMobileCameraView
              onFiles={handleWebFiles}
              Fallback={WebMobilePickView}
            />
          ) : (
            <PickView onTake={takePhoto} onPick={pickFromLibrary} />
          )}
        </>
      ) : phase === 'done' ? (
        <DoneView
          acceptedItems={acceptedItemsForDone}
          primaryPhotos={primaryPhotos}
          fallbackRoomPhotoUrl={sceneCtx.storage_url}
          onFinish={handleFinishDone}
          onScanAgain={handleScanAgainFromDone}
        />
      ) : (
        <>
          <ScrollView
            style={styles.pageScroll}
            contentContainerStyle={styles.pageScrollBody}
            scrollEnabled={!immersive}
            showsVerticalScrollIndicator
          >
            {/* ── First screen: viewport-height immersive camera ── */}
            <View
              style={[
                styles.firstScreen,
                { height: Dimensions.get('window').height },
              ]}
            >
              <View style={styles.stageFullscreen}>
                <ImageAnnotationStage
                  imageUrl={selected!.uri}
                  items={annotationItems}
                  onToggleSelected={handleToggleSelected}
                  onFocusBbox={handleFocusBbox}
                  onRefineItem={handleEnterRefine}
                  onDeleteItem={handleDeleteItem}
                  focusedItemId={focusedItemId}
                  editingItemId={editingItemId}
                  zoomRef={zoomStageRef}
                  fillParent
                >
                  {({ transform, containerRef }) => (
                    <>
                      {phase === 'scanning' ? (
                        <ScanningHero statusText={scanStatusText} />
                      ) : null}
                      {refining && editingItemId != null && editBbox ? (
                        <RefineOverlay
                          itemId={editingItemId}
                          initialBbox={editBbox}
                          transform={transform}
                          containerRef={containerRef}
                          mode={refineMode}
                          points={points}
                          nextPointLabel={nextPointLabel}
                          onAddPoint={handleAddPoint}
                          onChange={(b, d) => {
                            setEditBbox(b);
                            setEditDirty(d);
                          }}
                        />
                      ) : null}
                      <DrawNewItemOverlay
                        active={
                          (drawingNew || annotatingItemId !== null) &&
                          !draftBbox &&
                          !refining
                        }
                        transform={transform}
                        containerRef={containerRef}
                        onCommit={
                          annotatingItemId !== null
                            ? handleAnnotateCommit
                            : // AI tap-add (#411): a meaningful-size drag
                              // skips NameDraftItemSheet and goes straight
                              // through manualAnalyze (BE fills the name).
                              // The old draw-then-name flow is gone for
                              // new items; rename happens after via the
                              // existing meta-edit sheet.
                              runTapAddAnalyze
                        }
                        onCancel={handleDraftCancel}
                        onTap={
                          // Only wire onTap for the manual-add path —
                          // annotate-existing still wants real drags.
                          // Inlined here because `transform.imageSize`
                          // is only in scope inside the stage's
                          // render-prop closure.
                          annotatingItemId !== null
                            ? undefined
                            : (anchor, meta) => {
                                const imgSize = transform.imageSize;
                                if (!imgSize) return;
                                const bbox = manufactureSquareBbox(
                                  anchor.x,
                                  anchor.y,
                                  imgSize.width,
                                  imgSize.height,
                                );
                                runTapAddAnalyze(bbox, meta);
                              }
                        }
                        onTapMiss={
                          annotatingItemId !== null
                            ? undefined
                            : () => {
                                Toast.show({
                                  type: 'info',
                                  text1: 'Tap on the photo',
                                  text2:
                                    'That tap missed the image — aim inside the frame.',
                                });
                              }
                        }
                      />
                      {/* Per-tap ghost overlays (#411). One per
                          in-flight AI tap-add round. */}
                      {tapAddInflight.map((rec) => (
                        <TapAddGhost
                          key={rec.id}
                          record={rec}
                          transform={transform}
                          onDismiss={handleTapAddDismiss}
                          onRetry={handleTapAddRetry}
                        />
                      ))}
                    </>
                  )}
                </ImageAnnotationStage>
              </View>

              {/* Header chip + close button — absolute, top.
                  Gap #121 (2026-05-27 audit): the room label was rendered
                  raw ("DINING_ROOM") because room_context comes back from
                  the BE as an enum slug. Pretty-print to title case +
                  spaces. Gap #122: bump z-index + use opaque surface
                  background so the chip doesn't read-through with the
                  scanning-header text behind it. Also dropped the dev-y
                  "masks" segment — users care about items + selected
                  count, not the internal mask metric. */}
              {!immersive ? (
                <View style={styles.headerChip}>
                  {sceneCtx.room_context ? (
                    <>
                      <MaterialIcons
                        name="place"
                        size={12}
                        color={fpColors.primary}
                      />
                      <Text style={styles.headerChipRoom}>
                        {formatRoomLabel(sceneCtx.room_context)}
                      </Text>
                      <View style={styles.headerChipSep} />
                    </>
                  ) : null}
                  <Text style={styles.headerChipStatsValue}>
                    {scanItems.length}
                  </Text>
                  <Text style={styles.headerChipStatsLabel}>items</Text>
                  <View style={styles.headerChipSep} />
                  <Text
                    style={[
                      styles.headerChipStatsValue,
                      acceptedCount > 0 && { color: fpColors.primary },
                    ]}
                  >
                    {acceptedCount}
                  </Text>
                  <Text style={styles.headerChipStatsLabel}>selected</Text>
                </View>
              ) : null}

              {/* Option A HUD layout — close button floats alone
                  on row 1 right. GPS chip sits on row 2 left (its
                  own absolute container, independent of the room
                  chip so they don't overlap on truncation). */}
              {!immersive ? (
                <>
                  <View style={styles.topBarOverlay}>
                    <Pressable
                      onPress={handleCloseToHome}
                      hitSlop={8}
                      style={styles.closeBtnOverlay}
                      accessibilityRole="button"
                      accessibilityLabel="Close and return to dashboard"
                    >
                      <MaterialIcons
                        name="close"
                        size={20}
                        color={fpColors.onSurface}
                      />
                    </Pressable>
                  </View>
                  {scanGps ? (
                    <View
                      style={styles.gpsChip}
                      accessibilityLabel={`Scan location captured: ${scanGps.lat.toFixed(4)}, ${scanGps.lng.toFixed(4)}`}
                      testID="scan-gps-chip"
                    >
                      <MaterialIcons
                        name="location-on"
                        size={14}
                        color={fpColors.primary}
                      />
                      <Text style={styles.gpsChipText} numberOfLines={1}>
                        {scanGps.lat.toFixed(4)}, {scanGps.lng.toFixed(4)}
                      </Text>
                    </View>
                  ) : null}
                </>
              ) : null}

              {/* Bottom dock — toolbar stacked ABOVE the carousel
                  so they never overlap. Save pill lives here too
                  so the user can confirm without scrolling down. */}
              {!immersive ? (
                <View style={styles.carouselDock}>
                  <View style={styles.floatingToolbar}>
                    <Pressable
                      onPress={() => setInfoOpen(true)}
                      disabled={
                        !sceneCtx.room_context && !sceneCtx.ai_description
                      }
                      style={({ pressed }) => [
                        styles.toolbarBtn,
                        !sceneCtx.room_context &&
                          !sceneCtx.ai_description && { opacity: 0.4 },
                        pressed && { opacity: 0.85 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Image info"
                    >
                      <MaterialIcons
                        name="info-outline"
                        size={18}
                        color={fpColors.onSurface}
                      />
                    </Pressable>
                    <Pressable
                      onPress={handleEnterDraw}
                      disabled={!sceneCtx.file_record_id}
                      style={({ pressed }) => [
                        styles.toolbarBtn,
                        !sceneCtx.file_record_id && { opacity: 0.4 },
                        pressed && { opacity: 0.85 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Add a missed item"
                    >
                      <MaterialIcons
                        name="add"
                        size={20}
                        color={fpColors.primary}
                      />
                    </Pressable>
                    {/* #170 #3 — Share entry inline with Info / + / Save. */}
                    <Pressable
                      onPress={() => setShareOpen(true)}
                      style={({ pressed }) => [
                        styles.toolbarShareBtn,
                        pressed && { opacity: 0.85 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Share"
                      testID="share-to-groups-chip"
                    >
                      <MaterialIcons
                        name="group-add"
                        size={16}
                        color={fpColors.primary}
                      />
                      <Text style={styles.toolbarShareText} numberOfLines={1}>
                        {shareGroupCount > 0 ? `Share · ${shareGroupCount}` : 'Share'}
                      </Text>
                    </Pressable>
                    {/* Primary CTA — sized to read at a glance
                        without dwarfing the secondary icons. */}
                    <Pressable
                      onPress={handleSave}
                      disabled={
                        acceptedCount === 0 || streaming
                      }
                      style={({ pressed }) => [
                        styles.toolbarSaveBtn,
                        (acceptedCount === 0 || streaming) && {
                          opacity: 0.45,
                        },
                        pressed && acceptedCount > 0 && { opacity: 0.88 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={`Save ${acceptedCount} items`}
                    >
                      <Text style={styles.toolbarSaveText}>
                        {streaming
                          ? 'Scanning\u2026'
                          : `Save ${acceptedCount}`}
                      </Text>
                      {!streaming && acceptedCount > 0 ? (
                        <MaterialIcons
                          name="arrow-forward"
                          size={14}
                          color={fpColors.onPrimary}
                        />
                      ) : null}
                    </Pressable>
                  </View>
                  {/* #170 #3 — Share moved inline into the toolbar row above
                      (Info / + / Share / Save). No separate chip row. */}
                  <FocusedItemCarousel
                    items={railItems}
                    focusedItemId={focusedItemId}
                    pageWidth={viewportWidth}
                    streaming={streaming}
                    onFocusChange={setFocusedItemId}
                    onToggleSelected={handleToggleSelected}
                    onEditDetails={handleEditDetails}
                    onRefineBbox={handleEnterRefine}
                    onDeleteItem={handleDeleteItem}
                    onAnnotate={handleEnterAnnotate}
                    // FE-214 — `renderItemFooter` left unwired; the
                    // slot stays open for future plug-ins.
                  />
                </View>
              ) : null}
            </View>

            {/* ── Second screen: the full vertical list + CTAs ── */}
            {!immersive ? (
              <View style={styles.secondScreen}>
                <Text style={styles.secondScreenHeading}>All items</Text>
                <ItemRail
                  items={railItems}
                  onToggleSelected={handleToggleSelected}
                  onEditDetails={handleEditDetails}
                  onRefineBbox={handleEnterRefine}
                  onDeleteItem={handleDeleteItem}
                  onAnnotate={handleEnterAnnotate}
                  focusedItemId={focusedItemId}
                  streaming={streaming}
                />
                {errorMsg ? (
                  <View style={styles.errorBanner}>
                    <MaterialIcons
                      name="error-outline"
                      size={14}
                      color={fpColors.error}
                    />
                    <Text style={styles.errorBannerText} numberOfLines={2}>
                      {errorMsg}
                    </Text>
                  </View>
                ) : null}
                <View style={styles.secondScreenCtaRow}>
                  <Pressable
                    onPress={reset}
                    hitSlop={4}
                    style={({ pressed }) => [
                      styles.sheetNewBtn,
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Pick a new photo"
                  >
                    <MaterialIcons
                      name="refresh"
                      size={14}
                      color={fpColors.onSurface}
                    />
                    <Text style={styles.sheetNewText}>New photo</Text>
                  </Pressable>
                  <Pressable
                    onPress={handleSave}
                    disabled={
                      acceptedCount === 0 || streaming
                    }
                    style={({ pressed }) => [
                      styles.sheetSaveBtn,
                      (acceptedCount === 0 || streaming) && {
                        opacity: 0.45,
                      },
                      pressed && acceptedCount > 0 && { opacity: 0.88 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Save ${acceptedCount} items`}
                  >
                    <Text style={styles.sheetSaveText}>
                      {streaming
                        ? 'Scanning\u2026'
                        : `Save ${acceptedCount}`}
                    </Text>
                    {!streaming && acceptedCount > 0 ? (
                      <MaterialIcons
                        name="arrow-forward"
                        size={14}
                        color={fpColors.onPrimary}
                      />
                    ) : null}
                  </Pressable>
                </View>
              </View>
            ) : null}
          </ScrollView>

          {/* Draw-mode hint banner — a floating pill near the top
              when drawing. */}
          {drawingNew && !draftBbox ? (
            <View style={styles.drawHintFloat}>
              <MaterialIcons
                name="crop-free"
                size={14}
                color={fpColors.primary}
              />
              <Text style={styles.drawHintText}>
                Tap or drag a missed item.
              </Text>
              <Pressable
                onPress={handleExitDraw}
                hitSlop={6}
                style={styles.drawingCancelBtn}
                accessibilityRole="button"
                accessibilityLabel="Exit draw"
              >
                <Text style={styles.drawingCancelText}>Cancel</Text>
              </Pressable>
            </View>
          ) : null}

          {/* Annotate-mode hint — user drew from a rail card's
              Annotate chip. Draws the bbox, server rasterises it. */}
          {annotatingItemId !== null && !draftBbox ? (
            <View style={styles.drawHintFloat}>
              <MaterialIcons
                name="gesture"
                size={14}
                color={fpColors.primary}
              />
              <Text style={styles.drawHintText}>
                Drag on the photo to outline this item.
              </Text>
              <Pressable
                onPress={handleExitAnnotate}
                hitSlop={6}
                style={styles.drawingCancelBtn}
                accessibilityRole="button"
                accessibilityLabel="Exit annotate"
              >
                <Text style={styles.drawingCancelText}>Cancel</Text>
              </Pressable>
            </View>
          ) : null}

          {/* Refine hint — floating pill at top during refine mode. */}
          {refining ? (
            <View style={styles.refineHintFloat}>
              <MaterialIcons
                name={refineMode === 'box' ? 'open-with' : 'auto-fix-high'}
                size={14}
                color={fpColors.primary}
              />
              <Text style={styles.refineHintText}>
                {refineMode === 'box'
                  ? 'Drag to move. Pull any handle to resize.'
                  : points.length === 0
                    ? 'Tap the part of the item to keep.'
                    : `${points.length} point${points.length === 1 ? '' : 's'}`}
              </Text>
            </View>
          ) : null}
        </>
      )}

      {/* Top refine toolbar — mode + polarity + point count. */}
      <RefineToolbar
        visible={refining}
        mode={refineMode}
        onModeChange={setRefineMode}
        nextPointLabel={nextPointLabel}
        onPolarityChange={setNextPointLabel}
        pointCount={points.length}
        maxPoints={POINT_CAP}
        onClearPoints={handleClearPoints}
      />

      {/* Floating Apply / Cancel — portaled to body so the stage's
          overflow:hidden can't clip it. Adaptive label per PRD §8.8. */}
      <FloatingActionBar
        visible={refining}
        applyLabel={
          refineMode === 'points' && points.length > 0
            ? '\u2728 Smart refit (SAM3)'
            : '\u2713 Save my box'
        }
        onApply={handleApplyRefine}
        onCancel={handleCancelRefine}
        loading={applyBusy}
        disabled={
          refineMode === 'points'
            ? points.length === 0
            : !editDirty
        }
      />

      {/* Text-metadata edit modal — reuses the existing component
          that PATCHes /users/inventory/{id}. onSaved feeds the
          updated item back into the slice. */}
      <EditItemSheet
        visible={metaEditItem !== null}
        item={metaEditItem as any}
        onClose={() => setMetaEditItemId(null)}
        onSaved={handleMetaSaved}
      />

      {/* Name-this-item sheet — opens after the user releases a
          meaningful-size draft bbox. On Save we POST /inventory/manual
          and the server returns a fully-formed item+mask. */}
      <NameDraftItemSheet
        visible={drawingNew && draftBbox !== null}
        saving={adding}
        onCancel={handleExitDraw}
        onSave={handleDraftSave}
      />

      {/* Image info modal — the floating Info toolbar button opens
          this. Shows room_context + ai_description. */}
      <ImageInfoSheet
        visible={infoOpen}
        roomContext={sceneCtx.room_context}
        aiDescription={sceneCtx.ai_description}
        stillScanning={streaming}
        onClose={() => setInfoOpen(false)}
      />
      {/* #170 — unified ShareSheet (scan mode). Per-group item selection
          + "share with someone new". Staged lazily; applied at Save via
          confirm/batch extras. Items not picked save to Personal only. */}
      {shareOpen ? (
        <ShareSheet
          visible
          onClose={() => setShareOpen(false)}
          mode="scan"
          items={acceptedItemsList}
          sourceImageUrl={selected?.uri ?? null}
          onApply={(plan) => {
            setSharePlan(plan);
            setShareOpen(false);
          }}
        />
      ) : null}
    </View>
  );
};


/* ————— FE #124.6 — Resume banner ————— *
 *
 * Renders at the top of the SegmentScreen body when the screen was
 * opened with `?resume=true&file_record_id={n}` route params.
 * One pill per status with a CTA that routes to Scan History
 * (full SSE re-attach + Redux seed is the next layer, blocked on
 * backend#313).
 *
 * Status branches:
 *   COMPLETED       — handled by an effect upstream that redirects
 *                     to SceneViewer; banner never renders.
 *   READY_TO_SAVE   — detected items waiting to be saved.
 *   IN_PROGRESS     — segmentation still running on the server.
 *   FAILED          — last attempt errored; retry available once
 *                     backend#313 ships.
 *   CANCELLED       — user previously cancelled; treated like FAILED.
 *   loading         — pre-fetch state.
 *   error           — fetch failed; surfaces the BE detail.
 */

type ResumeStatus =
  | 'IN_PROGRESS'
  | 'READY_TO_SAVE'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

const resumeStatusCopy = (
  status: ResumeStatus,
  detected: number,
  saved: number,
): { title: string; subtitle: string; tone: 'live' | 'ready' | 'failed' } => {
  switch (status) {
    case 'IN_PROGRESS':
      return {
        title: 'Scan still running',
        subtitle:
          detected > 0
            ? `${detected} item${detected === 1 ? '' : 's'} detected so far · open Scan history to watch live`
            : 'Open Scan history to watch live progress',
        tone: 'live',
      };
    case 'READY_TO_SAVE':
      return {
        title: 'Ready to save',
        subtitle:
          detected > 0
            ? `${detected} item${detected === 1 ? '' : 's'} detected · ${detected - saved} not saved yet`
            : 'Detected items waiting on your review',
        tone: 'ready',
      };
    case 'FAILED':
      return {
        title: 'Scan failed',
        subtitle:
          'We hit an error mid-way. Retry from Scan history once the server is back.',
        tone: 'failed',
      };
    case 'CANCELLED':
      return {
        title: 'Scan cancelled',
        subtitle:
          'This scan was cancelled. Start a fresh photo below to try again.',
        tone: 'failed',
      };
    case 'COMPLETED':
    default:
      return {
        title: 'Scan complete',
        subtitle: 'Opening the snapshot…',
        tone: 'ready',
      };
  }
};

const ResumeBanner: React.FC<{
  loading: boolean;
  retrying: boolean;
  error: string | null;
  banner:
    | {
        status: ResumeStatus;
        detected_count: number;
        saved_count: number;
        room_context: string | null;
        filename: string | null;
        fileRecordId: number;
      }
    | null;
  onOpenHistory: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}> = ({
  loading,
  retrying,
  error,
  banner,
  onOpenHistory,
  onRetry,
  onDismiss,
}) => {
  if (loading) {
    return (
      <View style={resumeBannerStyles.wrap}>
        <View
          style={[resumeBannerStyles.card, resumeBannerStyles.cardLoading]}
        >
          <MaterialIcons
            name="hourglass-empty"
            size={20}
            color={fpColors.onSurfaceVariant}
          />
          <Text style={resumeBannerStyles.loadingText}>
            Loading scan details…
          </Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={resumeBannerStyles.wrap}>
        <View
          style={[resumeBannerStyles.card, resumeBannerStyles.cardFailed]}
        >
          <MaterialIcons name="error-outline" size={20} color="#B3261E" />
          <View style={resumeBannerStyles.body}>
            <Text style={resumeBannerStyles.titleFailed}>
              Couldn’t load this scan
            </Text>
            <Text style={resumeBannerStyles.subtitle} numberOfLines={2}>
              {error}
            </Text>
          </View>
          <Pressable
            onPress={onDismiss}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Dismiss"
            style={resumeBannerStyles.dismissBtn}
          >
            <MaterialIcons
              name="close"
              size={18}
              color={fpColors.onSurfaceVariant}
            />
          </Pressable>
        </View>
      </View>
    );
  }

  if (!banner) return null;
  if (banner.status === 'COMPLETED') return null;

  const copy = resumeStatusCopy(
    banner.status,
    banner.detected_count,
    banner.saved_count,
  );

  const toneStyle =
    copy.tone === 'live'
      ? resumeBannerStyles.cardLive
      : copy.tone === 'ready'
        ? resumeBannerStyles.cardReady
        : resumeBannerStyles.cardFailed;
  const iconName: keyof typeof MaterialIcons.glyphMap =
    copy.tone === 'live'
      ? 'sync'
      : copy.tone === 'ready'
        ? 'check-circle-outline'
        : 'error-outline';
  const iconColor =
    copy.tone === 'live'
      ? '#0D47A1'
      : copy.tone === 'ready'
        ? '#1B5E20'
        : '#B3261E';

  // FAILED + CANCELLED expose a [Retry] CTA; the other statuses
  // expose [Open] which routes to /scans. Retry is only shown
  // once — the banner is dismissed after a successful replay.
  const isRetryable =
    banner.status === 'FAILED' || banner.status === 'CANCELLED';

  return (
    <View style={resumeBannerStyles.wrap}>
      <View style={[resumeBannerStyles.card, toneStyle]}>
        <MaterialIcons name={iconName} size={22} color={iconColor} />
        <View style={resumeBannerStyles.body}>
          <Text
            style={
              copy.tone === 'failed'
                ? resumeBannerStyles.titleFailed
                : resumeBannerStyles.title
            }
          >
            {copy.title}
          </Text>
          <Text style={resumeBannerStyles.subtitle} numberOfLines={2}>
            {copy.subtitle}
          </Text>
        </View>
        {isRetryable ? (
          <Pressable
            onPress={retrying ? undefined : onRetry}
            disabled={retrying}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Retry scan"
            accessibilityState={{ disabled: retrying, busy: retrying }}
            style={[
              resumeBannerStyles.cta,
              retrying && resumeBannerStyles.ctaDisabled,
            ]}
          >
            <MaterialIcons
              name={retrying ? 'hourglass-empty' : 'refresh'}
              size={16}
              color="#B3261E"
            />
            <Text
              style={[resumeBannerStyles.ctaText, { color: '#B3261E' }]}
            >
              {retrying ? 'Retrying…' : 'Retry'}
            </Text>
          </Pressable>
        ) : (
          <Pressable
            onPress={onOpenHistory}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Open Scan history"
            style={resumeBannerStyles.cta}
          >
            <Text style={resumeBannerStyles.ctaText}>Open</Text>
            <MaterialIcons name="chevron-right" size={18} color="#1A237E" />
          </Pressable>
        )}
      </View>
    </View>
  );
};

const resumeBannerStyles = StyleSheet.create({
  wrap: {
    // Float above whatever phase is active (pick / scanning /
    // review). Anchored below the page-chrome top bar (~56px) so
    // the [back] arrow stays tappable. zIndex puts it above the
    // immersive camera + stage views.
    position: 'absolute',
    top: 56,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
    zIndex: 50,
    // Surface gets a tiny shadow so it visually separates from
    // whichever phase view is rendered behind it.
    elevation: 4,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  cardLoading: {
    backgroundColor: fpColors.surfaceContainerLow,
    borderColor: fpColors.outlineVariant,
  },
  cardLive: {
    backgroundColor: '#E3F2FD',
    borderColor: '#90CAF9',
  },
  cardReady: {
    backgroundColor: '#E8F5E9',
    borderColor: '#A5D6A7',
  },
  cardFailed: {
    backgroundColor: '#FFEBEE',
    borderColor: '#EF9A9A',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  titleFailed: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#B3261E',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  loadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(26, 35, 126, 0.16)',
  },
  ctaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: '#1A237E',
  },
  ctaDisabled: {
    opacity: 0.5,
  },
  dismissBtn: {
    padding: 4,
  },
});


/* ————— Web-desktop pick: drag-drop hero + paste + browse ————— */

const WebDesktopDropPickView: React.FC<{
  onFiles: (files: File[]) => void;
}> = ({ onFiles }) => {
  const [dragging, setDragging] = useState(false);
  const dropRef = useRef<View>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openBrowse = () => {
    if (!fileInputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.style.display = 'none';
      input.addEventListener('change', () => {
        const files = Array.from(input.files ?? []);
        // Reset so picking the same file twice still fires `change`.
        input.value = '';
        if (files.length) onFiles(files);
      });
      document.body.appendChild(input);
      fileInputRef.current = input;
    }
    fileInputRef.current.click();
  };

  // Native DOM listeners on the zone — RN's onDrop synthetic event
  // doesn't expose dataTransfer.files reliably across browsers.
  useEffect(() => {
    const node = dropRef.current as unknown as HTMLElement | null;
    if (!node) return;
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!node.contains(e.relatedTarget as Node | null)) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    };
    node.addEventListener('dragover', onDragOver);
    node.addEventListener('dragleave', onDragLeave);
    node.addEventListener('drop', onDrop);
    return () => {
      node.removeEventListener('dragover', onDragOver);
      node.removeEventListener('dragleave', onDragLeave);
      node.removeEventListener('drop', onDrop);
    };
  }, [onFiles]);

  // Window-level ⌘V/Ctrl+V paste — works even when the zone isn't
  // focused. Image MIME filter prevents bogus uploads from text paste.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const files = items
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (files.length) onFiles(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onFiles]);

  // Prevent the browser from opening a stray drop outside the zone
  // (which would navigate away from the SPA).
  useEffect(() => {
    const swallow = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  return (
    <View style={styles.dropWrap}>
      <View style={styles.dropHero}>
        <Text style={styles.dropTitle}>Add items</Text>
        <Text style={styles.dropSubtitle}>
          Drop photos here, click to browse, or paste from your clipboard.
          Our AI identifies each item and adds it to your inventory.
        </Text>
      </View>

      <Pressable
        // @ts-ignore — RN Web forwards refs to the host element.
        ref={dropRef}
        onPress={openBrowse}
        accessibilityRole="button"
        accessibilityLabel="Drop photos or click to browse"
        style={[styles.dropZone, dragging && styles.dropZoneActive]}
        testID="segment-drop-zone"
      >
        <View style={styles.dropIconBubble}>
          <MaterialIcons
            name={dragging ? 'file-download' : 'cloud-upload'}
            size={40}
            color={fpColors.primary}
          />
        </View>
        <Text style={styles.dropZoneTitle}>
          {dragging ? 'Drop to scan' : 'Drop photos here'}
        </Text>
        <Text style={styles.dropZoneSubtitle}>
          or <Text style={styles.dropZoneLink}>click to browse</Text>
        </Text>
        <View style={styles.dropZoneKbdRow}>
          <View style={styles.kbd}>
            <Text style={styles.kbdText}>⌘V</Text>
          </View>
          <Text style={styles.dropZoneKbdHint}>paste a screenshot</Text>
        </View>
      </Pressable>

      <View style={styles.dropHint}>
        <MaterialIcons name="auto-awesome" size={14} color={fpColors.primary} />
        <Text style={styles.dropHintText}>
          Tip: a well-lit, straight-on shot helps the AI recognize more items.
        </Text>
      </View>
    </View>
  );
};

/* ————— Mobile-web pick: OS camera capture + library ————— */

const WebMobilePickView: React.FC<{
  onFiles: (files: File[]) => void;
}> = ({ onFiles }) => {
  const camInputRef = useRef<HTMLInputElement | null>(null);
  const libInputRef = useRef<HTMLInputElement | null>(null);

  // Two separate hidden inputs — `capture=environment` makes mobile
  // browsers open the rear camera directly; omitting it on the
  // library button lets the OS show the photo picker as usual.
  const openInput = (
    ref: React.MutableRefObject<HTMLInputElement | null>,
    capture: 'environment' | null,
  ) => {
    if (!ref.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      if (capture) input.setAttribute('capture', capture);
      input.style.display = 'none';
      input.addEventListener('change', () => {
        const files = Array.from(input.files ?? []);
        input.value = '';
        if (files.length) onFiles(files);
      });
      document.body.appendChild(input);
      ref.current = input;
    }
    ref.current.click();
  };

  return (
    <View style={styles.mobilePickWrap}>
      <View style={styles.mobilePickHero}>
        <View style={styles.mobilePickHeroIcon}>
          <MaterialIcons name="auto-awesome" size={28} color={fpColors.primary} />
        </View>
        <Text style={styles.mobilePickTitle}>Add items</Text>
        <Text style={styles.mobilePickSubtitle}>
          Snap a photo or pick one from your device. Our AI does the rest.
        </Text>
      </View>

      <Pressable
        onPress={() => openInput(camInputRef, 'environment')}
        accessibilityRole="button"
        accessibilityLabel="Take photo"
        testID="mobile-pick-camera"
        style={({ pressed }) => [
          styles.mobilePickAction,
          styles.mobilePickActionPrimary,
          pressed && { opacity: 0.88 },
        ]}
      >
        <MaterialIcons name="photo-camera" size={28} color={fpColors.onPrimary} />
        <View style={{ flex: 1 }}>
          <Text style={styles.mobilePickLabelPrimary}>Take photo</Text>
          <Text style={styles.mobilePickMetaPrimary}>Snap & scan</Text>
        </View>
        <MaterialIcons name="chevron-right" size={22} color={`${fpColors.onPrimary}c0`} />
      </Pressable>

      <Pressable
        onPress={() => openInput(libInputRef, null)}
        accessibilityRole="button"
        accessibilityLabel="From device"
        testID="mobile-pick-library"
        style={({ pressed }) => [
          styles.mobilePickAction,
          styles.mobilePickActionSecondary,
          pressed && { opacity: 0.88 },
        ]}
      >
        <MaterialIcons name="photo-library" size={28} color={fpColors.primary} />
        <View style={{ flex: 1 }}>
          <Text style={styles.mobilePickLabelSecondary}>From device</Text>
          <Text style={styles.mobilePickMetaSecondary}>Pick an existing photo</Text>
        </View>
        <MaterialIcons name="chevron-right" size={22} color={fpColors.outline} />
      </Pressable>
    </View>
  );
};

const PickView: React.FC<{
  onTake: () => void;
  onPick: () => void;
}> = ({ onTake, onPick }) => (
  <View style={styles.pick}>
    <View style={styles.pickHero}>
      <View style={styles.pickIcon}>
        <MaterialIcons
          name="auto-awesome"
          size={32}
          color={fpColors.primary}
        />
      </View>
      <Text style={styles.pickTitle}>Scan any room</Text>
      <Text style={styles.pickSubtitle}>
        Snap or pick a photo. We&apos;ll detect every item and draw masks
        right on the image.
      </Text>
    </View>
    <View style={styles.pickRow}>
      <Pressable
        onPress={onTake}
        style={({ pressed }) => [
          styles.pickAction,
          styles.pickActionPrimary,
          pressed && { opacity: 0.88 },
        ]}
      >
        <MaterialIcons name="photo-camera" size={26} color={fpColors.onPrimary} />
        <Text style={styles.pickActionLabelPrimary}>Take Photo</Text>
        <Text style={styles.pickActionMetaPrimary}>Snap & scan</Text>
      </Pressable>
      <Pressable
        onPress={onPick}
        style={({ pressed }) => [
          styles.pickAction,
          styles.pickActionSecondary,
          pressed && { opacity: 0.88 },
        ]}
      >
        <MaterialIcons
          name="photo-library"
          size={26}
          color={fpColors.primary}
        />
        <Text style={styles.pickActionLabelSecondary}>From Library</Text>
        <Text style={styles.pickActionMetaSecondary}>Pick an image</Text>
      </Pressable>
    </View>
  </View>
);

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // #163 — Resume-in-flight loading fill. Replaces the camera/pick view
  // for the ~300ms the scan detail is being fetched, so there's no
  // Take Photo flash between the tap and the scan stage.
  resumeLoadingFill: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    backgroundColor: fpColors.background,
  },
  resumeLoadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },

  // Fullscreen stage fills the viewport; sheet + topbar overlay it.
  stageFullscreen: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#000',
  },
  topBarOverlay: {
    // Row 1 right — close button only. Sits at the same vertical
    // baseline as the room headerChip on the left.
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 48 : 16,
    right: 16,
    zIndex: 10,
  },
  gpsChip: {
    // Row 2 left — sits directly under the room headerChip. The
    // headerChip uses `right: 72` to leave room for the close
    // button, so we mirror that constraint here for a clean stack.
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 100 : 68,
    left: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.94)',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    maxWidth: 260,
    zIndex: 10,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } as any)
      : null),
  },
  gpsChipText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
    letterSpacing: 0.2,
  },
  closeBtnOverlay: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.94)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } as any)
      : null),
  },
  headerChip: {
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 54 : 22,
    left: 16,
    right: 72,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    // Gap #122 (2026-05-27 audit) — was rgba(...,0.94); the 6%
    // translucency let the underlying status-row text peek through at
    // the chip seam (visible as "…r ph" in step-9 of journey 2 v1).
    // Bump to 0.99 — still preserves the iOS frosted-glass feel but
    // makes the chip readable as a solid pill.
    backgroundColor: 'rgba(255,255,255,0.99)',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    // Gap #122 — bump z-index well above the bbox overlay (which
    // sits at the default 0). zIndex:20 keeps the chip above any
    // chip / annotation that might be drawn near the top of the photo.
    zIndex: 20,
    overflow: 'hidden',
  },
  headerChipRoom: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.primary,
    letterSpacing: 0.6,
    // Gap #121 (2026-05-27 audit) — was textTransform:'uppercase',
    // which re-uppercased our pretty-printed "Dining room" back to
    // "DINING ROOM". Drop the transform so formatRoomLabel's output
    // (title-case) survives to the user.
  },
  headerChipSep: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: fpColors.outlineVariant,
    marginHorizontal: 5,
  },
  headerChipStatsValue: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
  },
  headerChipStatsLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 10,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.3,
    // Gap #121 — was uppercase ("ITEMS", "SELECTED"). Drop the
    // transform so lowercase labels ("items", "selected") read more
    // naturally next to the room name.
    marginLeft: 2,
  },

  // Floating refine / draw hint pills.
  refineHintFloat: {
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 110 : 80,
    left: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    zIndex: 10,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 4px 14px rgba(31,26,34,0.18)' } as any)
      : null),
  },
  refineHintText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurface,
  },
  drawHintFloat: {
    position: 'absolute' as any,
    top: Platform.OS === 'ios' ? 54 : 22,
    left: 16,
    right: 70,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.96)',
    borderWidth: 1,
    borderColor: `${fpColors.primary}40`,
    zIndex: 10,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 4px 14px rgba(31,26,34,0.18)' } as any)
      : null),
  },
  drawHintText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurface,
  },

  // Outer page scroll.
  pageScroll: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  pageScrollBody: {
    paddingBottom: 0,
  },

  // First screen (viewport-height immersive camera).
  firstScreen: {
    position: 'relative',
    width: '100%',
    backgroundColor: '#000',
  },
  floatingToolbar: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 10,
  },
  toolbarBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.5)',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 4px 14px rgba(0,0,0,0.32)',
          backdropFilter: 'blur(14px) saturate(160%)',
          WebkitBackdropFilter: 'blur(14px) saturate(160%)',
        } as any)
      : null),
  },
  // Container row for the SaveDestinationChip beneath the toolbar.
  // Tight margins so it visually attaches to the Save CTA above and
  // doesn't crowd the carousel below.
  toolbarDestinationRow: {
    marginTop: 8,
    marginBottom: 4,
    paddingHorizontal: 4,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  // #170 #3 — inline "Share" pill in the Info/+/Share/Save toolbar row.
  toolbarShareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
  },
  toolbarShareText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
  // #170 — "Share to groups" entry chip beneath the toolbar.
  shareEntryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
    maxWidth: '100%',
  },
  shareEntryChipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onPrimaryContainer,
  },
  toolbarSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 18,
    backgroundColor: fpColors.primary,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 6px 18px ${fpColors.primary}55`,
        } as any)
      : null),
  },
  toolbarSaveText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimary,
  },
  carouselDock: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    bottom: 16,
    zIndex: 9,
  },

  // Second screen (full list + CTAs).
  secondScreen: {
    paddingTop: 12,
    paddingBottom: 40,
    backgroundColor: fpColors.background,
  },
  secondScreenHeading: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: fpColors.onSurfaceVariant,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  secondScreenCtaRow: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // Retained CTA button styles (used in secondScreen CTA row).
  sheetSaveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: fpColors.primary,
  },
  sheetSaveText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimary,
  },
  sheetNewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  sheetNewText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurface,
  },

  aiDescCard: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
    marginHorizontal: 16,
    marginTop: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}0a`,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  aiDescText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 17,
    color: fpColors.onSurface,
  },

  ctaWrap: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 10,
  },
  addRow: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 4,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: fpColors.primary,
    backgroundColor: `${fpColors.primary}0a`,
  },
  addBtnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
    letterSpacing: 0.1,
  },
  drawingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}40`,
  },
  drawingText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurface,
  },
  drawingCancelBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  drawingCancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },

  pick: { flex: 1, padding: 16, paddingTop: 24 },
  pickHero: { alignItems: 'center', marginBottom: 28 },
  pickIcon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  pickTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 6,
  },
  pickSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    maxWidth: 320,
  },
  pickRow: { gap: 12, marginBottom: 20 },
  pickAction: {
    borderRadius: 16,
    padding: 20,
    minHeight: 140,
    justifyContent: 'space-between',
  },
  pickActionPrimary: {
    backgroundColor: fpColors.primary,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 8px 16px ${fpColors.primary}40`,
        } as any)
      : {}),
  },
  pickActionSecondary: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  pickActionLabelPrimary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onPrimary,
  },
  pickActionMetaPrimary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: `${fpColors.onPrimary}c0`,
  },
  pickActionLabelSecondary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  pickActionMetaSecondary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },

  /* ——— Web-desktop drop zone ——— */
  dropWrap: {
    paddingTop: 32,
    paddingBottom: 32,
    paddingHorizontal: 24,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    gap: 20,
  },
  dropHero: {
    alignItems: 'center',
    gap: 6,
  },
  dropTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 24,
    color: fpColors.onSurface,
  },
  dropSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.outline,
    textAlign: 'center',
    maxWidth: 480,
  },
  dropZone: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: fpColors.outlineVariant,
    borderRadius: 16,
    paddingVertical: 56,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
    gap: 12,
    // @ts-ignore — RN Web only
    cursor: 'pointer',
    // @ts-ignore — RN Web only
    transition: 'border-color 120ms, background-color 120ms',
  },
  dropZoneActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primaryContainer,
  },
  dropIconBubble: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
    marginBottom: 4,
  },
  dropZoneTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  dropZoneSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.outline,
  },
  dropZoneLink: {
    color: fpColors.primary,
    fontFamily: 'Inter_700Bold',
  },
  dropZoneKbdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  kbd: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surface,
  },
  kbdText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
    letterSpacing: 0.3,
  },
  dropZoneKbdHint: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
  },
  dropHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  dropHintText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
  },
  /* ——— Mobile-web pick view ——— */
  mobilePickWrap: {
    paddingTop: 24,
    paddingBottom: 32,
    paddingHorizontal: 20,
    gap: 14,
  },
  mobilePickHero: {
    alignItems: 'center',
    gap: 8,
    paddingTop: 8,
    paddingBottom: 12,
  },
  mobilePickHeroIcon: {
    width: 60,
    height: 60,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
    marginBottom: 4,
  },
  mobilePickTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onSurface,
  },
  mobilePickSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.outline,
    textAlign: 'center',
    maxWidth: 320,
  },
  mobilePickAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 18,
    paddingHorizontal: 18,
    borderRadius: 16,
  },
  mobilePickActionPrimary: {
    backgroundColor: fpColors.primary,
  },
  mobilePickActionSecondary: {
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  mobilePickLabelPrimary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onPrimary,
  },
  mobilePickMetaPrimary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: `${fpColors.onPrimary}c0`,
    marginTop: 2,
  },
  mobilePickLabelSecondary: {
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onSurface,
  },
  mobilePickMetaSecondary: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 2,
  },
});

// Silence Image import used by RN bundler only.
void Image;

// pickerModalStyles removed alongside the SaveDestinationPicker mount.
